// =============================================================
// Web i18n extension.
//
// @perpetuum/core ships a deliberate SCAFFOLD dictionary (common/nav/wo/
// tolerance). The web console needs more UI strings; rather than fork the
// translator, this layers app-specific keys on top of the core `t()` —
// core keys win where they exist (single source of truth for shared strings),
// app keys fill the rest. LV/RU/EN, live switching preserved.
// =============================================================

import { t as coreT, type Lang, type TVars } from "@perpetuum/core";
import { usePrefs } from "../app/PrefsContext.js";

type Dict = Record<string, string>;

/** Flat (dot-key) web-app dictionary, per language. EN is the fallback. */
const WEB_STRINGS: Record<Lang, Dict> = {
  en: {
    "app.name": "Perpetuum",
    "app.tagline": "Iterum CMMS / EAM",
    "login.title": "Sign in",
    "login.sub": "Plant maintenance console",
    "login.username": "Username",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.mfaTitle": "Two-factor verification",
    "login.mfaSub": "Enter the 6-digit code from your authenticator.",
    "login.mfaCode": "Verification code",
    "login.mfaSubmit": "Verify",
    "login.err.invalid_credentials": "Wrong username or password.",
    "login.err.invalid_code": "Invalid code — try again.",
    "login.err.network_error": "Cannot reach the server.",
    "nav.overview": "OVERVIEW",
    "nav.planning": "PLANNING",
    "nav.admin": "ADMIN",
    "nav.dashboard": "Reports & dashboard",
    "nav.scheduler": "PM scheduler",
    "nav.downtimes": "Downtime register",
    "nav.settings": "Settings & dictionaries",
    "nav.access": "People & access",
    "settings.title": "Settings & dictionaries",
    "settings.sub":
      "Every dropdown the system offers is configured here — one consistent managed-list pattern, not a maze of bespoke grids. Device categories also carry the custom-field schema (the 8-type engine).",
    // ---- WAVE 6: dictionaries hub ----
    "settings.dict.railLabel": "Dictionaries",
    "settings.dict.devcat": "Device categories",
    "settings.dict.devcatHint": "Asset types + their custom-field schema",
    "settings.dict.stocks": "Warehouses / stocks",
    "settings.dict.stocksHint": "Stock locations",
    "settings.dict.downcat": "Downtime categories",
    "settings.dict.downcatHint": "Causes for the downtime register",
    "settings.dict.taskstage": "Task stages",
    "settings.dict.taskstageHint": "The fixed work-order status set",
    "settings.dict.devmfr": "Device manufacturers",
    "settings.dict.sparecat": "Spare categories",
    "settings.dict.sparemfr": "Spare manufacturers",
    "settings.dict.taskgrp": "Task groups",
    "settings.dict.reportstatus": "Report statuses",
    "settings.dict.checklist": "Checklist presets",
    "settings.dict.tag.readonly": "read-only",
    "settings.dict.tag.soon": "soon",
    "settings.dict.col.name": "Name",
    "settings.dict.col.value": "Value",
    "settings.dict.col.main": "Main",
    "settings.dict.yes": "yes",
    "settings.dict.add": "Add",
    "settings.dict.edit": "Edit",
    "settings.dict.delete": "Delete",
    "settings.dict.editNamed": "Edit “{name}”",
    "settings.dict.deleteNamed": "Delete “{name}”",
    "settings.dict.create": "Create",
    "settings.dict.save": "Save changes",
    "settings.dict.newTitle": "New {group}",
    "settings.dict.editTitle": "Edit {group}",
    "settings.dict.namePlaceholder": "New {group} name…",
    "settings.dict.mainHint": "Main warehouse (default for receipts)",
    "settings.dict.added": "Added “{name}” to {group}",
    "settings.dict.updated": "Updated “{name}”",
    "settings.dict.deleted": "Deleted “{name}”",
    "settings.dict.deleteTitle": "Delete “{name}”?",
    "settings.dict.deleteBody":
      "This removes it from the {group} dictionary. Records already using it keep their value.",
    "settings.dict.writeErr": "Could not save — check your permissions.",
    "settings.dict.deleteErr": "Could not delete — check your permissions.",
    "settings.dict.empty": "No {group} yet.",
    "settings.dict.emptyHint": "Add the first entry to start using it in pickers.",
    "settings.dict.readonlyNote":
      "Task stages are a fixed, server-defined status set — they are not editable. They are shown here for reference.",
    "settings.dict.unbacked.title": "Not configurable yet",
    "settings.dict.unbacked.hint": "This dictionary has no master table.",
    "settings.dict.unbacked.note":
      "This dictionary has no dedicated tenant table on /api/v1 today. Manufacturers are free-text on devices and spares; task groups, report statuses and checklist presets are not yet backed. It will be wired in a later slice — never faked here.",
    // custom-field schema editor (the 8-type engine)
    "settings.cf.title": "Custom fields",
    "settings.cf.addField": "Add field",
    "settings.cf.newCatLabel": "New device category name",
    "settings.cf.newCatPlaceholder": "New device category, e.g. Extruder…",
    "settings.cf.noCats": "No device categories yet.",
    "settings.cf.noCatsHint": "Add a category, then attach its custom fields.",
    "settings.cf.deleteCatBody":
      "This removes the category and detaches its custom-field schema. Devices keep their stored values.",
    "settings.cf.none": "No custom fields on this category.",
    "settings.cf.noneHint": "Add a field to extend the device card for this asset type.",
    "settings.cf.newTitle": "New custom field",
    "settings.cf.editTitle": "Edit custom field",
    "settings.cf.label": "Field label",
    "settings.cf.labelPlaceholder": "e.g. Stator gap (mm)",
    "settings.cf.type": "Field type",
    "settings.cf.suffix": "Unit / suffix",
    "settings.cf.suffixPlaceholder": "e.g. mm, kW, °C (optional)",
    "settings.cf.required": "Required",
    "settings.cf.requiredHint": "Required on the device card",
    "settings.cf.options": "Select options",
    "settings.cf.optionsEmpty": "No options added.",
    "settings.cf.optionValue": "Option value",
    "settings.cf.optionPlaceholder": "Add an option value…",
    "settings.cf.addOption": "Add",
    "settings.cf.removeOption": "Remove option “{value}”",
    "settings.cf.save": "Save changes",
    "settings.cf.added": "Added field “{name}”",
    "settings.cf.updated": "Updated field “{name}”",
    "settings.cf.deleted": "Deleted field “{name}”",
    "settings.cf.editNamed": "Edit field “{name}”",
    "settings.cf.deleteNamed": "Delete field “{name}”",
    "settings.cf.deleteTitle": "Delete field “{name}”?",
    "settings.cf.deleteBody":
      "This removes the field from the category schema. Stored device values for this field are not deleted.",
    "settings.cf.writeErr": "Could not save the field — check your permissions.",
    "access.title": "People & access",
    "admin.title": "Admin console",
    // ---- WAVE 5: People & access (Users · Permissions · Companies · Departments) ----
    "access.sub":
      "Users, permission groups, companies and departments in one place. Permissions are plain access levels per module — read (View), create & modify (Edit), incl. delete & configuration (Full) — the same matrix the server enforces.",
    "access.tab.users": "Users",
    "access.tab.perms": "Permissions",
    "access.tab.companies": "Companies",
    "access.tab.depts": "Departments",
    // users
    "access.users.title": "Users",
    "access.users.add": "Add user",
    "access.users.new": "New user",
    "access.users.edit": "Edit user",
    "access.users.col.user": "User",
    "access.users.col.email": "Email",
    "access.users.col.group": "Groups",
    "access.users.col.active": "Active",
    "access.users.active": "Active",
    "access.users.inactive": "Inactive",
    "access.users.activate": "Activate",
    "access.users.deactivate": "Deactivate",
    "access.users.noGroup": "No group",
    "access.users.empty": "No users yet.",
    "access.users.emptyHint": "Create the first user to grant plant access.",
    "access.users.field.name": "Full name",
    "access.users.field.namePlaceholder": "e.g. I. Liepa",
    "access.users.field.username": "Username",
    "access.users.field.usernamePlaceholder": "username",
    "access.users.field.email": "Email",
    "access.users.field.lang": "Language",
    "access.users.field.department": "Department",
    "access.users.field.groups": "Permission groups",
    "access.users.field.groupsHint": "A user inherits the most-permissive level across all their groups.",
    "access.users.noDept": "No department",
    "access.users.create": "Create user",
    "access.users.save": "Save changes",
    "access.users.created": "User created: {name}",
    "access.users.updated": "User updated: {name}",
    "access.users.saveErr": "Could not save — check your permissions.",
    "access.users.createErr": "Could not create the user.",
    "access.users.activated": "{name} activated",
    "access.users.deactivated": "{name} deactivated",
    "access.users.deactivateTitle": "Deactivate user",
    "access.users.deactivateConfirm":
      "Deactivate “{name}”? They keep their account and history but can no longer sign in or be assigned new work.",
    "access.users.toggleErr": "Could not update the user.",
    "access.users.passwordNote":
      "Initial passwords are set by the user via a server-issued reset — this form never sends a plaintext password.",
    // permissions matrix
    "access.perms.group": "Permission group",
    "access.perms.save": "Save changes",
    "access.perms.saveTitle": "Save permission changes",
    "access.perms.saveConfirm": "Apply the edited access levels to “{name}”? Every member of the group is affected immediately.",
    "access.perms.saved": "Saved permissions for {name}",
    "access.perms.saveErr": "Could not save — check your permissions.",
    "access.perms.empty": "No permission groups.",
    "access.perms.emptyHint": "Create a group in User groups (RBAC) first.",
    "access.perms.menuVisible": "Visible in navigation",
    "access.perms.menuVisibleFor": "Menu visibility for {module}",
    "access.perms.levelFor": "{module} access level",
    "access.perms.note":
      "Access levels apply to every member of the group. View = read-only, Edit = create & modify, Full = incl. delete & configuration.",
    "access.perms.defaultsNote": "Showing the seeded levels. Pick a level per module, then save.",
    // companies
    "access.companies.title": "Companies",
    "access.companies.add": "Add company",
    "access.companies.new": "New company",
    "access.companies.edit": "Edit company",
    "access.companies.tenant": "single-tenant",
    "access.companies.col.name": "Company",
    "access.companies.col.currency": "Currency",
    "access.companies.col.active": "Active",
    "access.companies.empty": "No companies yet.",
    "access.companies.emptyHint": "Add the operating company to anchor users and assets.",
    "access.companies.field.name": "Company name",
    "access.companies.field.namePlaceholder": "e.g. A/S ITERUM",
    "access.companies.field.currency": "Currency",
    "access.companies.field.currencyPlaceholder": "e.g. EUR",
    "access.companies.create": "Create company",
    "access.companies.save": "Save changes",
    "access.companies.created": "Company added: {name}",
    "access.companies.updated": "Company updated: {name}",
    "access.companies.saveErr": "Could not save the company.",
    // departments
    "access.depts.title": "Departments",
    "access.depts.add": "Add department",
    "access.depts.new": "New department",
    "access.depts.edit": "Edit department",
    "access.depts.hint": "Child departments inherit from their parent.",
    "access.depts.empty": "No departments yet.",
    "access.depts.emptyHint": "Add the first division to organise assets and people.",
    "access.depts.field.name": "Department name",
    "access.depts.field.namePlaceholder": "e.g. Mechanical Division",
    "access.depts.field.parent": "Parent department",
    "access.depts.noParent": "No parent (top level)",
    "access.depts.create": "Create department",
    "access.depts.save": "Save changes",
    "access.depts.created": "Department added: {name}",
    "access.depts.updated": "Department updated: {name}",
    "access.depts.saveErr": "Could not save the department.",
    "access.depts.edit.btn": "Edit",
    "access.depts.delete": "Delete",
    "access.depts.deleteTitle": "Delete department",
    "access.depts.deleteConfirm":
      "Delete “{name}”? Any child departments are re-parented to the top level; assets keep their records.",
    "access.depts.deleted": "Department deleted: {name}",
    "access.depts.deleteErr": "Could not delete the department.",
    "tree.title": "Location & device tree",
    "tree.sub": "Browse the asset hierarchy; open any device for its full card.",
    "tree.empty": "No devices found.",
    "workboard.title": "Work board",
    "workboard.sub":
      "Pick what's urgent and open any work order. Overdue is shown relative to each machine's interval.",
    "workboard.col.wo": "Work order",
    "workboard.col.device": "Device",
    "workboard.col.status": "Status",
    "workboard.col.due": "Due · overdue",
    "workboard.col.priority": "Priority",
    "workboard.filter.all": "All",
    "workboard.filter.open": "Open",
    "workboard.filter.in_progress": "In progress",
    "workboard.filter.completed": "Completed",
    "workboard.empty": "Nothing matches this filter.",
    "workboard.complete": "Complete work order",
    "workboard.completed": "Work order completed",
    "workboard.loto": "Blocked by the LOTO gate (confirm lockout + checklist + sign-off).",
    "workboard.sortBy": "Sort by",
    "workboard.bulk.title": "Bulk actions",
    "workboard.bulk.selected": "{n} selected",
    "workboard.bulk.selectAll": "Select all work orders",
    "workboard.bulk.selectRow": "Select work order {wo}",
    "workboard.bulk.reassign": "Reassign…",
    "workboard.bulk.reassignTitle": "Reassign performer",
    "workboard.bulk.reassignFrom": "Current performer: {from}",
    "workboard.bulk.newPerformer": "New performer",
    "workboard.bulk.selectUser": "Select…",
    "workboard.bulk.reassignNeedsOne": "Select rows that share a single current performer to reassign.",
    "workboard.bulk.reassignScopeNote":
      "Reassignment is by performer: ALL of this performer's upcoming tasks move to the new performer (not only the selected rows). Completed tasks are untouched.",
    "workboard.bulk.reassignConfirmTitle": "Confirm reassignment",
    "workboard.bulk.reassignConfirm": "Reassign {n} upcoming task(s) from {from} to {to}?",
    "workboard.bulk.reassigned": "Reassigned {n} task(s)",
    "workboard.bulk.reschedule": "Reschedule…",
    "workboard.bulk.rescheduleTitle": "Reschedule work orders",
    "workboard.bulk.rescheduleDate": "New due date",
    "workboard.bulk.rescheduleAvoidWeekend": "Avoid weekends (shift Sat/Sun to Friday)",
    "workboard.bulk.rescheduleConfirmTitle": "Confirm reschedule",
    "workboard.bulk.rescheduleConfirm": "Move {n} selected work order(s) to {date}?",
    "workboard.bulk.rescheduled": "Rescheduled {n} work order(s)",
    "workboard.bulk.rescheduleNone": "None of the selected work orders can be rescheduled (completed/cancelled are skipped).",
    "workboard.bulk.clear": "Clear selection",
    // ---- WAVE 3: work-order detail (execution surface) ----
    "wo.details": "Details",
    "wo.device": "Device",
    "wo.service": "Service",
    "wo.priority": "Priority",
    "wo.due": "Due",
    "wo.performer": "Performer",
    "wo.unassigned": "Unassigned",
    "wo.noDevice": "Asset",
    "wo.noLocation": "—",
    "wo.checklist": "Checklist",
    "wo.checklist.empty": "No checklist steps yet.",
    "wo.checklist.add": "Add step",
    "wo.checklist.addPlaceholder": "Add a checklist step…",
    "wo.checklist.delete": "Delete step “{label}”",
    "wo.notes": "Notes",
    "wo.notes.placeholder": "Service notes — what was done, parts used, observations…",
    "wo.notes.save": "Save notes",
    "wo.notesSaved": "Notes saved",
    "wo.timer.label": "Task timer",
    "wo.timer.running": "Running",
    "wo.timer.start": "Start",
    "wo.timer.stop": "Stop",
    "wo.timer.total": "Total logged",
    "wo.timer.entries": "Logged work-time entries",
    "wo.timer.elapsed": "Elapsed time {time}",
    "wo.timerStarted": "Timer started",
    "wo.timerStopped": "Timer stopped",
    "wo.timerAlreadyRunning": "A timer is already running for you on this work order.",
    "wo.signOff": "Complete & sign off",
    "wo.block.loto": "Blocked: confirm lock-out / tag-out first.",
    "wo.block.noChecklist": "Blocked: add at least one checklist step.",
    "wo.block.checklist": "Blocked: every checklist step must be done.",
    "wo.block.signed": "Blocked: the work order must be signed off.",
    // ---- WAVE 4: tasks register ----
    "register.title": "Tasks register",
    "register.sub":
      "The complete, strategic register of every work order — paged across the whole task set. Sort, filter and export; open any row for its full work-order detail.",
    "register.search": "Search number or title",
    "register.filter.status": "Status filter",
    "register.filter.priority": "Priority filter",
    "register.filter.allStatus": "All statuses",
    "register.filter.allPriority": "All priorities",
    "register.filter.overdue": "Overdue only",
    "register.status.paused": "Paused",
    "register.status.cancelled": "Cancelled",
    "register.status.postponed": "Postponed",
    "register.prio.critical": "Critical",
    "register.prio.high": "High",
    "register.prio.normal": "Normal",
    "register.prio.low": "Low",
    "register.col.wo": "WO #",
    "register.col.title": "Title",
    "register.col.device": "Device",
    "register.col.status": "Status",
    "register.col.priority": "Priority",
    "register.col.assignee": "Assignee",
    "register.col.due": "Due date",
    "register.col.overdue": "Overdue",
    "register.unassigned": "Unassigned",
    "register.overdueFlag": "overdue",
    "register.overdueDays": "+{n}d",
    "register.overdueTitle": "Past its due date",
    "register.selectAll": "Select all rows on this page",
    "register.selectRow": "Select work order {wo}",
    "register.export.all": "Export CSV",
    "register.export.selected": "Export CSV ({n})",
    "register.empty": "No work orders.",
    "register.emptyHint": "Nothing matches the current filters on this page.",
    "register.range.of": "{from}–{to} of {total}",
    "register.range.window": "Showing {from}–{to}",
    "register.range.none": "No rows",
    "register.prev": "Previous",
    "register.next": "Next",
    // ---- WAVE 4 (audit fix): canonical work-order status chip labels (shared
    // by the work board + tasks register STATUS_CHIP maps) + id-resolution
    // fallbacks (never show a raw UUID / blank). ----
    "status.open": "Open",
    "status.in_progress": "In progress",
    "status.paused": "Paused",
    "status.completed": "Completed",
    "status.cancelled": "Cancelled",
    "status.postponed": "Postponed",
    "common.unknownUser": "(unknown)",
    "scheduler.service": "Service",
    "scheduler.priority": "Priority",
    "scheduler.close": "Close",
    "downtimes.uncategorized": "(uncategorized)",
    // ---- WAVE 4: executors & teams ----
    "executors.title": "Executors & teams",
    "executors.sub":
      "Performers are users. Activate or deactivate them, edit their details, and add new performers. Deactivated performers drop out of new-task pickers but stay in every historical record.",
    "executors.add": "Add performer",
    "executors.create": "Create performer",
    "executors.save": "Save performer",
    "executors.field.name": "Display name",
    "executors.field.namePlaceholder": "Full name",
    "executors.field.username": "Username",
    "executors.field.email": "Email",
    "executors.field.group": "Group / role",
    "executors.noGroup": "No group",
    "executors.active": "Active",
    "executors.inactive": "Inactive",
    "executors.activePerformer": "Active performer",
    "executors.activeHint":
      "When off, {name} disappears from new-task assignment pickers — but every past work order they completed keeps their name for the audit trail.",
    "executors.empty": "No performers yet.",
    "executors.selectHint": "Select a performer to view and edit their details.",
    "executors.saved": "Saved {name}",
    "executors.saveErr": "Could not save — check your permissions.",
    "executors.created": "Performer created: {name}",
    "executors.createErr": "Could not create performer.",
    "executors.activated": "{name} activated",
    "executors.deactivated": "{name} deactivated",
    // ---- WAVE 4: user groups ----
    "groups.title": "User groups & roles (RBAC)",
    "groups.sub":
      "Role-based access groups. Create, rename and remove groups here; the full read/write/delete permission matrix is edited in People & access.",
    "groups.groups": "Groups",
    "groups.new": "New group",
    "groups.newTitle": "New permission group",
    "groups.editTitle": "Edit group",
    "groups.field.name": "Group name",
    "groups.field.namePlaceholder": "e.g. Night-shift technician",
    "groups.matrixNote": "The read/write/delete permission matrix is configured in People & access.",
    "groups.members": "{n} users",
    "groups.noPerms": "No permissions granted yet.",
    "groups.edit": "Edit",
    "groups.delete": "Delete",
    "groups.deleteTitle": "Delete group",
    "groups.deleteConfirm": "Delete the group “{name}”? Users keep their accounts but lose this group's access.",
    "groups.create": "Create group",
    "groups.save": "Save changes",
    "groups.created": "Group created: {name}",
    "groups.updated": "Group updated: {name}",
    "groups.deleted": "Group deleted: {name}",
    "groups.saveErr": "Could not save the group.",
    "groups.deleteErr": "Could not delete the group.",
    "groups.empty": "No permission groups.",
    "groups.emptyHint": "Create the first group to start assigning access.",
    "inventory.title": "Spares inventory & warehouse ledger",
    "inventory.sub": "Available = In stock − Booked (reserved on open work orders).",
    "inventory.inStock": "In stock",
    "inventory.booked": "Booked",
    "inventory.available": "Available",
    "inventory.book": "Book",
    "inventory.qty": "Quantity",
    "inventory.confirm": "Confirm booking",
    "inventory.booked.ok": "Booked {n} against {name}",
    "inventory.overbook": "Cannot book more than available.",
    "inventory.item": "Item",
    "inventory.noSpares": "No spare parts yet.",
    "inventory.noSparesHint": "Add spares in the dictionaries to start booking, receiving and transferring stock.",
    "inventory.readonly": "You have read-only access to spares — actions are disabled.",
    "inventory.tab.book": "Book",
    "inventory.tab.receipt": "Goods receipt",
    "inventory.tab.writeoff": "Write-off",
    "inventory.tab.transfer": "Transfer",
    "inventory.tab.import": "Excel import",
    "inventory.receiptTitle": "Goods receipt — stock intake",
    "inventory.supplier": "Supplier",
    "inventory.docNr": "Invoice / delivery note №",
    "inventory.intakeTs": "Intake timestamp",
    "inventory.intakeNote": "Intake timestamp is generated in real time by the server. Historic stock modification is blocked.",
    "inventory.qtyReceived": "Quantity received",
    "inventory.registerIntake": "Register intake",
    "inventory.received.ok": "Received {n} of {name}",
    "inventory.receiptRealtime": "Goods receipt must be recorded in real time (server timestamp only).",
    "inventory.writeoffTitle": "Write-off — safe inventory reduction",
    "inventory.qtyWriteOff": "Quantity to write off",
    "inventory.confirmWriteOff": "Confirm write-off",
    "inventory.wroteOff.ok": "Wrote off {n} of {name}",
    "inventory.writeoffCap": "Cannot write off more than is in stock.",
    "inventory.writeoffCapHint": "Quantity exceeds on-hand stock ({inStock}). The server caps write-offs at available stock — negative balances are protected.",
    "inventory.transferTitle": "Stock-to-stock transfer",
    "inventory.fromWarehouse": "From warehouse",
    "inventory.toWarehouse": "To warehouse",
    "inventory.availTransfer": "Available to transfer",
    "inventory.transferred.ok": "Transferred {n} of {name}",
    "inventory.transferCap": "Cannot transfer more than is available (reserved stock stays put).",
    "inventory.transferCapHint": "Only the {available} available may move — reserved stock stays put.",
    "inventory.transferNote": "Transfers move stock between locations without changing total on-hand. Only the available quantity (in stock − booked) can be moved; both warehouse ledgers update on confirm.",
    "inventory.confirmTransfer": "Confirm transfer",
    "inventory.importTitle": "Excel import — pre-flight validation",
    "inventory.importLimit": "Max 3000 rows / transaction",
    "inventory.importPaste": "Paste rows: <ID><tab or comma><name>, one per line",
    "inventory.runPreflight": "Run preflight",
    "inventory.validation": "Validation",
    "inventory.reason": "Reason",
    "inventory.preflightDone": "Preflighted {n} rows",
    "inventory.preflightSummary": "{create} create · {update} update · {violation} security-violation",
    "scheduler.title": "PM scheduler",
    "scheduler.sub":
      "Drag-and-drop preventive-maintenance calendar. Weekend tasks shift when avoidance is on; overdue is judged relative to each machine's interval. The rolling forecast controls how far ahead scheduled work is shown.",
    "scheduler.machine": "Machine",
    "scheduler.unassigned": "Unassigned",
    "scheduler.avoidWeekend": "Avoid weekends",
    "scheduler.forecast": "Rolling forecast",
    "scheduler.empty": "No scheduled tasks in this window.",
    "scheduler.dragHint": "drag to reschedule",
    "scheduler.dragToReschedule": "Drag this bar to another day to reschedule the work order.",
    "scheduler.rescheduled": "Rescheduled to the {n}th",
    "scheduler.shifted": "Target fell on a weekend — shifted to the prior Friday.",
    "scheduler.scheduled": "Scheduled",
    "scheduler.foot": "Drag a task bar to a day to write its new due date. Weekend targets shift off Sat/Sun when avoidance is on.",
    "scheduler.furtherOut": "{n} more scheduled beyond the visible window.",
    "scheduler.noPerm": "You don't have permission to reschedule tasks.",
    "downtimes.title": "Downtime register",
    "downtimes.sub":
      "Every out-of-service period, by cause — the raw material for availability, MTBF and the failure reports. Log an event in seconds.",
    "downtimes.kpi.total": "Total downtime",
    "downtimes.kpi.events": "Events logged",
    "downtimes.kpi.down": "Currently down",
    "downtimes.log": "Downtime log",
    "downtimes.logBtn": "Log downtime",
    "downtimes.all": "All",
    "downtimes.col.asset": "Asset",
    "downtimes.col.cause": "Cause",
    "downtimes.col.started": "Started",
    "downtimes.col.duration": "Duration",
    "downtimes.ongoing": "ongoing",
    "downtimes.statusLabel": "Status",
    "downtimes.resolved": "Resolved",
    "downtimes.empty": "No downtime logged yet.",
    "downtimes.emptyHint": "Log the first out-of-service period to start building availability and MTBF.",
    "downtimes.byCause": "By cause",
    "downtimes.noCats": "No downtime categories defined.",
    "downtimes.kpi.mtbf": "MTBF · mean time between failures",
    "downtimes.kpi.availability": "Availability",
    "downtimes.kpi.na": "n/a",
    "downtimes.kpi.naHint": "Needs at least one closed downtime to derive.",
    "downtimes.note": "Note",
    "downtimes.notePlaceholder": "Brief cause / symptom",
    "downtimes.register": "Register downtime",
    "downtimes.logged": "Downtime logged",
    "downtimes.noPerm": "You don't have permission to log downtime.",
    "downtimes.col.actions": "Actions",
    "downtimes.convert": "Convert to work order",
    "downtimes.convertTitle": "Convert downtime to work order?",
    "downtimes.convertBody": "Raise a new work order from this fault ({asset}). It opens in the Work board.",
    "downtimes.convertConfirm": "Create work order",
    "downtimes.converted": "Work order {number} created",
    "downtimes.convertErr": "Couldn't convert this downtime to a work order.",
    "downtimes.convertNoPerm": "You don't have permission to create work orders.",
    "dc.measure.add": "Add reading",
    "dc.measure.value": "Value",
    "dc.measure.recorded": "Reading recorded",
    "dc.measure.lastReading": "last reading vs alarm/warning bands",
    "dc.measure.noReading": "no reading yet",
    "dc.files.add": "Attach file",
    "dc.files.upload": "Upload",
    "dc.files.none": "No documents attached.",
    "dc.files.added": "File attached",
    "dc.downtime.add": "Log downtime",
    "dc.downtime.none": "No downtime recorded for this device.",
    "dc.history.none": "No history yet.",
    "common.cancel": "Cancel",
    "dc.tab.overview": "Overview",
    "dc.tab.maintenance": "Maintenance",
    "dc.tab.measurements": "Measurements",
    "dc.tab.downtimes": "Downtimes",
    "dc.tab.files": "Files",
    "dc.tab.spares": "Spares",
    "dc.tab.history": "History",
    "dc.openWos": "Open work orders",
    "dc.identity": "Identity",
    "dc.status": "Status",
    "dc.serial": "Serial / inventory",
    "dc.location": "Location",
    "dc.workHours": "Operating hours",
    "dashboard.title": "Reports & dashboard",
    "dashboard.sub":
      "Plant-wide maintenance health at a glance — uptime, MTBF, schedule compliance and parts spend, computed from the live reports. Underivable figures show n/a, never fabricated numbers.",
    "dashboard.na": "n/a",
    "dashboard.naHint": "Not derivable from the available report data yet.",
    "dashboard.kpi.uptime": "Asset availability",
    "dashboard.kpi.mtbf": "MTBF · mean time between failures",
    "dashboard.kpi.completion": "Completed work orders",
    "dashboard.kpi.partsSpend": "Parts spend (purchases)",
    "dashboard.repairs": "Repairs — planned vs unplanned",
    "dashboard.planned": "Planned",
    "dashboard.unplanned": "Unplanned",
    "dashboard.noRepairs": "No completed work orders to chart yet.",
    "dashboard.downtimeByLine": "Downtime by asset",
    "dashboard.totalDowntime": "total",
    "dashboard.noDowntime": "No downtime recorded yet.",
    "dashboard.topAssets": "Top assets by maintenance load",
    "dashboard.topAssetsHint": "by total downtime hours",
    "dashboard.noAssets": "No asset downtime to rank yet.",
    "dashboard.assetMeta": "{failures} failure(s)",
    "dashboard.assetLoad": "{hours} h downtime",
    "reports.title": "Reports",
    "reports.sub": "Real aggregation from the live data. Filter, then export to Excel/CSV or PDF.",
    "reports.from": "From",
    "reports.to": "To",
    "reports.run": "Run report",
    "reports.exportCsv": "Export CSV",
    "reports.exportPdf": "Export PDF",
    "reports.empty": "No rows for these filters.",
    "reports.emptyHint": "Widen the date range or pick a different report.",
    "reports.r.mtbf": "MTBF",
    "reports.r.mttr": "MTTR",
    "reports.r.downtime": "Downtime",
    "reports.r.working-time": "Working time",
    "reports.r.parts-spend": "Parts spend",
    "reports.r.tasks-repairs": "Tasks & repairs",
    "reports.col.deviceId": "Device",
    "reports.col.userId": "Technician",
    "reports.col.taskId": "Work order",
    "reports.col.failures": "Failures",
    "reports.col.downtimeHours": "Downtime",
    "reports.col.repairHours": "Repair time",
    "reports.col.workHours": "Working time",
    "reports.col.startedAt": "Started",
    "reports.col.endedAt": "Ended",
    "reports.col.reason": "Reason",
    "reports.col.spare": "Part",
    "reports.col.name": "Name",
    "reports.col.movementType": "Movement",
    "reports.col.quantity": "Qty",
    "reports.col.lineValue": "Value",
    "reports.col.docDate": "Date",
    "reports.col.number": "No.",
    "reports.col.title": "Title",
    "reports.col.status": "Status",
    "reports.col.priority": "Priority",
    "reports.col.completedAt": "Completed",
    "common.loading": "Loading…",
    "common.signedInAs": "Signed in",
    "common.theme": "Theme",
    "common.close": "Close",
    // ---- app frame / chrome (nav layout, profile) ----
    "nav.collapse": "Collapse",
    "nav.expand": "Expand sidebar",
    "nav.autoCollapsed": "Auto-collapsed (narrow)",
    "profile.myProfile": "My profile",
    "profile.preferences": "Preferences",
    "profile.toggleTheme": "Toggle theme",
    "profile.group": "Group",
    "profile.company": "Company",
    "profile.username": "Username",
    "profile.email": "Email",
    "profile.language": "Language",
    "settings.nav.title": "Navigation",
    "settings.nav.layout": "Navigation layout",
    "settings.nav.layoutHint":
      "Choose where the menu lives. A side rail suits dense planner work; a top bar frees vertical space for tables and schematics.",
    "settings.nav.side": "Sidebar",
    "settings.nav.top": "Top bar",
    "settings.nav.width": "Sidebar width",
    "settings.nav.widthHint":
      "Expanded shows labels; collapsed is an icon rail with hover tooltips. Auto-collapses on narrow windows.",
    "settings.nav.expanded": "Expanded",
    "settings.nav.collapsed": "Collapsed",
    "status.version": "Perpetuum v6.0 (build slice 6)",
    "status.online": "API online",
    // ---- global chrome (Phase 9.3) ----
    "nav.review": "Review Center",
    "review.title": "Review Center",
    "review.sub": "Offline changes that couldn't be applied automatically — their target was deleted or reassigned. Apply to force them through, or discard.",
    "chrome.search": "Search",
    "chrome.offline": "Offline (cached)",
    "chrome.pending": "pending sync",
    // ---- WAVE 7: measurement templates + meter readings ----
    "nav.measTemplates": "Measurement templates",
    "nav.meters": "Meter readings",
    "measTpl.title": "Measurement templates",
    "measTpl.sub": "Define the LL/L/H/HH tolerance bands per measurement point, grouped into templates bound to device categories.",
    "measTpl.list": "Templates",
    "measTpl.add": "New template",
    "measTpl.addTitle": "New measurement template",
    "measTpl.name": "Template name",
    "measTpl.namePlaceholder": "e.g. Extruder vibration",
    "measTpl.category": "Device category",
    "measTpl.categoryNone": "— (unbound)",
    "measTpl.empty": "No measurement templates yet",
    "measTpl.emptyHint": "Create a template, then add its measurement points with tolerance bands.",
    "measTpl.rename": "Rename template",
    "measTpl.deleteTitle": "Delete template?",
    "measTpl.deleteBody": "This removes the template and all of its measurement points. Recorded readings are kept.",
    "measTpl.selectHint": "Select a template to edit its points",
    "measTpl.points": "Measurement points",
    "measTpl.pointAdd": "Add point",
    "measTpl.pointName": "Point name",
    "measTpl.pointNamePlaceholder": "e.g. Bearing DE",
    "measTpl.unit": "Unit",
    "measTpl.unitPlaceholder": "e.g. mm/s",
    "measTpl.pointsEmpty": "No points on this template yet",
    "measTpl.pointsEmptyHint": "Add a point with its LL/L/H/HH thresholds; a preview bar shows the bands.",
    "measTpl.pointEdit": "Edit point",
    "measTpl.pointDeleteTitle": "Delete point?",
    "measTpl.pointDeleteBody": "This removes the measurement point and its tolerance bands.",
    "measTpl.preview": "Band preview",
    "measTpl.thresholdOrder": "Thresholds must satisfy LL ≤ L ≤ H ≤ HH.",
    "measTpl.col.point": "Point",
    "measTpl.col.unit": "Unit",
    "measTpl.col.bands": "LL / L / H / HH",
    "meters.title": "Meter readings",
    "meters.sub": "Pick a device, then record a reading for each of its measurement points. Each value is checked against its tolerance band in real time.",
    "meters.device": "Device",
    "meters.devicePlaceholder": "Select a device…",
    "meters.noDevice": "Pick a device to record readings",
    "meters.noDeviceHint": "Its measurement points (from the bound category template) load here.",
    "meters.noPoints": "No measurement points for this device",
    "meters.noPointsHint": "Bind the device's category to a measurement template with points, then come back.",
    "meters.point": "Point",
    "meters.latest": "Latest",
    "meters.newValue": "New reading",
    "meters.record": "Record",
    "meters.recorded": "Reading recorded",
    "meters.inTolerance": "In tolerance",
    "meters.warn": "Out of warning band",
    "meters.alarm": "Out of alarm band — investigate",
    "meters.noReading": "No reading yet",
    "meters.recent": "Recent readings",
    "meters.recentEmpty": "No readings recorded yet",
    "meters.noPerm": "You don't have permission to record readings.",
    // ---- admin console (i18n completion) ----
    "admin.sub": "Mass operations, per-tenant feature toggles, security (IP allow/block + audit trail), and the Dynamics 365 Business Central integration hub.",
    "admin.tabsLabel": "Admin tabs",
    "admin.tab.flags": "Feature flags",
    "admin.tab.mass": "Mass replace",
    "admin.tab.security": "Security",
    "admin.tab.bc": "Dynamics 365 BC",
    "admin.flag.enabled": "{name} enabled",
    "admin.flag.disabled": "{name} disabled",
    "admin.flag.toggleErr": "Could not toggle — check your permissions.",
    "admin.mass.step.current": "Current performer",
    "admin.mass.step.scope": "Scope",
    "admin.mass.step.new": "New performer",
    "admin.mass.step.confirm": "Confirm",
    "admin.mass.select": "Select…",
    "admin.mass.filterLoc": "Filter by location (optional)",
    "admin.mass.allLocations": "All locations",
    "admin.mass.reassigned": "Reassigned {n} task(s)",
    "admin.mass.reassignErr": "Could not reassign — check your permissions.",
    "admin.mass.confirmLead": "Reassign",
    "admin.mass.confirmTrail": "upcoming task(s) to the new performer. Completed tasks are untouched.",
    "admin.mass.back": "Back",
    "admin.mass.continue": "Continue",
    "admin.mass.transfer": "Transfer {n} task(s)",
    "admin.sec.whitelisted": "Whitelisted {cidr}",
    "admin.sec.invalidCidr": "Invalid CIDR / IP.",
    "admin.sec.addErr": "Could not add range.",
    "admin.sec.unblockTitle": "Unblock {ip}?",
    "admin.sec.unblockBody": "This IP will be allowed to authenticate again.",
    "admin.sec.unblockConfirm": "Unblock IP",
    "admin.sec.unblocked": "Unblocked {ip}",
    "admin.sec.unblockErr": "Could not unblock.",
    "admin.sec.testInvalidIp": "Enter a single IPv4 address (no CIDR).",
    "admin.sec.testErr": "Could not test IP.",
    "admin.sec.exportErr": "Could not export — check your permissions.",
    "admin.sec.blocklist": "Active blocklist",
    "admin.sec.blockedCount": "{n} blocked",
    "admin.sec.colIp": "IP address",
    "admin.sec.colReason": "Reason",
    "admin.sec.unblock": "Unblock",
    "admin.sec.noBlocked": "No blocked addresses",
    "admin.sec.testTitle": "Test an IP",
    "admin.sec.testHint": "check allow / block against the current rules",
    "admin.sec.testInputLabel": "IP address to test",
    "admin.sec.testPlaceholder": "e.g. 10.4.22.187",
    "admin.sec.test": "Test",
    "admin.sec.wouldBe": "would be",
    "admin.sec.allowed": "ALLOWED",
    "admin.sec.blocked": "BLOCKED",
    "admin.sec.reasonAllowlisted": "(exempt via allow range {ranges})",
    "admin.sec.reasonBlocked": "(matched block rule {rules})",
    "admin.sec.reasonDefaultAllow": "(no rule matches — default allow)",
    "admin.sec.allowedRanges": "Allowed ranges (CIDR)",
    "admin.sec.removeRange": "Remove {cidr}",
    "admin.sec.newRangeLabel": "New CIDR range",
    "admin.sec.cidrPlaceholder": "e.g. 172.16.0.0/12",
    "admin.sec.addRange": "Add range",
    "admin.sec.auditTrail": "Audit trail",
    "admin.sec.exportCsv": "Export CSV",
    "admin.sec.filterFrom": "From",
    "admin.sec.filterTo": "To",
    "admin.sec.filterActor": "Actor id",
    "admin.sec.filterActorPlaceholder": "user / api-key id",
    "admin.sec.filterAction": "Action",
    "admin.sec.filterActionPlaceholder": "e.g. Blocked",
    "admin.sec.noDiff": "Old → new value detail is not recorded by the audit log; the columns below are everything captured.",
    "admin.sec.colTime": "Time",
    "admin.sec.colActor": "Actor",
    "admin.sec.colTarget": "Target",
    "admin.sec.colIpShort": "IP",
    "admin.sec.colAction": "Action",
    "admin.sec.tagMobile": "mobile",
    "admin.sec.noAudit": "No audit entries match the current filters.",
    "admin.bc.title": "Item synchronisation · Dynamics 365 BC",
    "admin.bc.note": "The BC product id is read-only — we map to it, never rename it. Regenerating the owning API key terminates the integration, so its mappings compute as Orphaned.",
    "admin.bc.colSpare": "Spare",
    "admin.bc.colProductId": "Dynamics Product ID",
    "admin.bc.colStatus": "Status",
    "admin.bc.state.synced": "Synced",
    "admin.bc.state.out_of_sync": "Out-of-sync",
    "admin.bc.state.orphaned": "Orphaned",
    "admin.bc.empty": "No spare↔item mappings yet. Link a spare to a BC item to start syncing.",
    // ---- review center (i18n completion) ----
    "review.reason.target_deleted": "Target deleted",
    "review.reason.target_reassigned": "Reassigned away",
    "review.tab.open": "Open",
    "review.tab.resolved": "Resolved",
    "review.emptyOpen": "No parked changes — everything synced cleanly.",
    "review.emptyResolved": "No resolved items yet.",
    "review.applyTitle": "Apply parked change?",
    "review.discardTitle": "Discard parked change?",
    "review.applyBody": "Force-apply this {op} on {table}. This overrides the delete/reassign that parked it.",
    "review.discardBody": "Discard this {op} on {table}. The parked change will be dropped.",
    "review.applyConfirm": "Apply",
    "review.discardConfirm": "Discard",
    "review.applied": "Change applied",
    "review.discarded": "Change discarded",
    "review.notPermitted": "Not permitted (needs tasks Full)",
    "review.resolveErr": "Could not resolve ({code})",
    "review.colChange": "Change",
    "review.colWas": "Was",
    "review.apply": "Apply",
    "review.discard": "Discard",
    "review.resolved": "resolved",
    // ---- device card (i18n completion) ----
    "dc.close": "Close",
    "dc.nfc": "NFC",
    "dc.sectionsLabel": "Device card sections",
    "dc.status.operational": "In operation",
    "dc.status.maintenance": "Maintenance",
    "dc.status.down": "Down",
    "dc.status.decommissioned": "Decommissioned",
    "dc.noOpenWos": "No open work orders.",
    "dc.maint.intervals": "Maintenance intervals",
    "dc.maint.kind.counter": "Counter",
    "dc.maint.kind.time": "Time",
    "dc.maint.onSchedule": "on schedule",
    "dc.maint.none": "No maintenance intervals scheduled.",
    "dc.measure.points": "Measurement points",
    "dc.measure.noTemplate": "No measurement template bound to this device.",
    "dc.measure.normal": "normal",
    "dc.downtime.log": "Downtime log",
    "dc.downtime.default": "Downtime",
    "dc.downtime.ongoing": "ongoing",
    "dc.downtime.min": "{n} min",
    "dc.files.documents": "Documents",
    "dc.files.colFile": "File",
    "dc.files.colType": "Type",
    "dc.files.colAdded": "Added",
    "dc.files.namePlaceholder": "manual.pdf",
    "dc.files.urlPlaceholder": "https://… or data:…",
    "dc.history.timeline": "Unified timeline",
    "dc.spares.mapped": "Mapped spare parts",
    "dc.spares.hint": "Available = In stock − Booked (reserved on open work orders)",
    "dc.spares.colCode": "Code",
    "dc.spares.colPart": "Part",
    "dc.spares.colInStock": "In stock",
    "dc.spares.colBooked": "Booked",
    "dc.spares.colAvailable": "Available",
    "dc.spares.colMin": "Min",
    "dc.spares.colStatus": "Status",
    "dc.spares.belowMin": "Below min",
    "dc.spares.ok": "OK",
    "dc.spares.none": "No spare parts mapped to this device.",
    // ---- command palette / notifications (i18n completion) ----
    "cmdk.label": "Command palette",
    "cmdk.placeholder": "Search assets, work orders, spares, people…",
    "cmdk.searchLabel": "Search",
    "cmdk.resultsLabel": "Search results",
    "cmdk.hint": "Type to search across the whole plant.",
    "cmdk.esc": "Esc",
    "cmdk.noMatches": "No matches for “{q}”.",
    "notif.label": "Notifications",
    "notif.unread": "unread",
    "notif.bell": "Notifications",
    "notif.bellUnread": "Notifications ({n} unread)",
    "notif.markAll": "Mark all read",
    "notif.empty": "You're all caught up.",
    "notif.ago.justNow": "just now",
    "notif.ago.min": "{n}m ago",
    "notif.ago.hour": "{n}h ago",
    "notif.ago.day": "{n}d ago",
  },
  lv: {
    "login.title": "Pieslēgties",
    "login.sub": "Rūpnīcas uzturēšanas konsole",
    "login.username": "Lietotājvārds",
    "login.password": "Parole",
    "login.submit": "Pieslēgties",
    "login.mfaTitle": "Divfaktoru verifikācija",
    "login.mfaSub": "Ievadiet 6 ciparu kodu no autentifikatora.",
    "login.mfaCode": "Verifikācijas kods",
    "login.mfaSubmit": "Apstiprināt",
    "login.err.invalid_credentials": "Nepareizs lietotājvārds vai parole.",
    "login.err.invalid_code": "Nederīgs kods — mēģiniet vēlreiz.",
    "settings.title": "Iestatījumi un vārdnīcas",
    "settings.sub":
      "Šeit konfigurē katru sistēmas nolaižamo sarakstu — viens vienots pārvaldīta saraksta princips, nevis pielāgotu režģu labirints. Iekārtu kategorijas satur arī pielāgoto lauku shēmu (8 tipu dzinējs).",
    "settings.dict.railLabel": "Vārdnīcas",
    "settings.dict.devcat": "Iekārtu kategorijas",
    "settings.dict.devcatHint": "Iekārtu tipi + to pielāgoto lauku shēma",
    "settings.dict.stocks": "Noliktavas / krājumi",
    "settings.dict.stocksHint": "Krājumu atrašanās vietas",
    "settings.dict.downcat": "Dīkstāves kategorijas",
    "settings.dict.downcatHint": "Cēloņi dīkstāves reģistram",
    "settings.dict.taskstage": "Uzdevumu posmi",
    "settings.dict.taskstageHint": "Fiksētais darba uzdevumu statusu kopums",
    "settings.dict.devmfr": "Iekārtu ražotāji",
    "settings.dict.sparecat": "Rezerves daļu kategorijas",
    "settings.dict.sparemfr": "Rezerves daļu ražotāji",
    "settings.dict.taskgrp": "Uzdevumu grupas",
    "settings.dict.reportstatus": "Atskaišu statusi",
    "settings.dict.checklist": "Kontrolsarakstu sagataves",
    "settings.dict.tag.readonly": "tikai lasāms",
    "settings.dict.tag.soon": "drīzumā",
    "settings.dict.col.name": "Nosaukums",
    "settings.dict.col.value": "Vērtība",
    "settings.dict.col.main": "Galvenā",
    "settings.dict.yes": "jā",
    "settings.dict.add": "Pievienot",
    "settings.dict.edit": "Rediģēt",
    "settings.dict.delete": "Dzēst",
    "settings.dict.editNamed": "Rediģēt “{name}”",
    "settings.dict.deleteNamed": "Dzēst “{name}”",
    "settings.dict.create": "Izveidot",
    "settings.dict.save": "Saglabāt izmaiņas",
    "settings.dict.newTitle": "Jauns: {group}",
    "settings.dict.editTitle": "Rediģēt: {group}",
    "settings.dict.namePlaceholder": "Jauns {group} nosaukums…",
    "settings.dict.mainHint": "Galvenā noliktava (noklusējums saņemšanai)",
    "settings.dict.added": "Pievienots “{name}” sadaļā {group}",
    "settings.dict.updated": "Atjaunināts “{name}”",
    "settings.dict.deleted": "Dzēsts “{name}”",
    "settings.dict.deleteTitle": "Dzēst “{name}”?",
    "settings.dict.deleteBody":
      "Tas izņem ierakstu no {group} vārdnīcas. Ieraksti, kas to jau izmanto, saglabā vērtību.",
    "settings.dict.writeErr": "Nevarēja saglabāt — pārbaudiet tiesības.",
    "settings.dict.deleteErr": "Nevarēja dzēst — pārbaudiet tiesības.",
    "settings.dict.empty": "Vēl nav: {group}.",
    "settings.dict.emptyHint": "Pievienojiet pirmo ierakstu, lai to izmantotu izvēlēs.",
    "settings.dict.readonlyNote":
      "Uzdevumu posmi ir fiksēts, servera definēts statusu kopums — tos nevar rediģēt. Šeit tie parādīti informācijai.",
    "settings.dict.unbacked.title": "Pagaidām nav konfigurējams",
    "settings.dict.unbacked.hint": "Šai vārdnīcai nav galvenās tabulas.",
    "settings.dict.unbacked.note":
      "Šai vārdnīcai šobrīd nav atsevišķas nomnieka tabulas /api/v1. Ražotāji ir brīvs teksts iekārtām un rezerves daļām; uzdevumu grupas, atskaišu statusi un kontrolsarakstu sagataves vēl nav nodrošinātas. Tās tiks pievienotas vēlāk — šeit nekas netiek izdomāts.",
    "settings.cf.title": "Pielāgotie lauki",
    "settings.cf.addField": "Pievienot lauku",
    "settings.cf.newCatLabel": "Jaunas iekārtu kategorijas nosaukums",
    "settings.cf.newCatPlaceholder": "Jauna iekārtu kategorija, piem., Ekstrūderis…",
    "settings.cf.noCats": "Vēl nav iekārtu kategoriju.",
    "settings.cf.noCatsHint": "Pievienojiet kategoriju, tad piesaistiet tās pielāgotos laukus.",
    "settings.cf.deleteCatBody":
      "Tas dzēš kategoriju un atvieno tās pielāgoto lauku shēmu. Iekārtas saglabā saglabātās vērtības.",
    "settings.cf.none": "Šai kategorijai nav pielāgotu lauku.",
    "settings.cf.noneHint": "Pievienojiet lauku, lai paplašinātu šī tipa iekārtu karti.",
    "settings.cf.newTitle": "Jauns pielāgotais lauks",
    "settings.cf.editTitle": "Rediģēt pielāgoto lauku",
    "settings.cf.label": "Lauka nosaukums",
    "settings.cf.labelPlaceholder": "piem., Statora atstarpe (mm)",
    "settings.cf.type": "Lauka tips",
    "settings.cf.suffix": "Mērvienība / sufikss",
    "settings.cf.suffixPlaceholder": "piem., mm, kW, °C (neobligāti)",
    "settings.cf.required": "Obligāts",
    "settings.cf.requiredHint": "Obligāts iekārtas kartē",
    "settings.cf.options": "Izvēles opcijas",
    "settings.cf.optionsEmpty": "Nav pievienotu opciju.",
    "settings.cf.optionValue": "Opcijas vērtība",
    "settings.cf.optionPlaceholder": "Pievienot opcijas vērtību…",
    "settings.cf.addOption": "Pievienot",
    "settings.cf.removeOption": "Noņemt opciju “{value}”",
    "settings.cf.save": "Saglabāt izmaiņas",
    "settings.cf.added": "Pievienots lauks “{name}”",
    "settings.cf.updated": "Atjaunināts lauks “{name}”",
    "settings.cf.deleted": "Dzēsts lauks “{name}”",
    "settings.cf.editNamed": "Rediģēt lauku “{name}”",
    "settings.cf.deleteNamed": "Dzēst lauku “{name}”",
    "settings.cf.deleteTitle": "Dzēst lauku “{name}”?",
    "settings.cf.deleteBody":
      "Tas noņem lauku no kategorijas shēmas. Saglabātās iekārtu vērtības šim laukam netiek dzēstas.",
    "settings.cf.writeErr": "Nevarēja saglabāt lauku — pārbaudiet tiesības.",
    "access.title": "Cilvēki un piekļuve",
    "access.sub":
      "Lietotāji, atļauju grupas, uzņēmumi un departamenti vienuviet. Atļaujas ir vienkārši piekļuves līmeņi katram modulim — lasīšana (Skatīt), izveide un labošana (Rediģēt), ieskaitot dzēšanu un konfigurāciju (Pilna) — tā pati matrica, ko ievieš serveris.",
    "access.tab.users": "Lietotāji",
    "access.tab.perms": "Atļaujas",
    "access.tab.companies": "Uzņēmumi",
    "access.tab.depts": "Departamenti",
    "access.users.title": "Lietotāji",
    "access.users.add": "Pievienot lietotāju",
    "access.users.new": "Jauns lietotājs",
    "access.users.edit": "Rediģēt lietotāju",
    "access.users.col.user": "Lietotājs",
    "access.users.col.email": "E-pasts",
    "access.users.col.group": "Grupas",
    "access.users.col.active": "Aktīvs",
    "access.users.active": "Aktīvs",
    "access.users.inactive": "Neaktīvs",
    "access.users.activate": "Aktivizēt",
    "access.users.deactivate": "Deaktivizēt",
    "access.users.noGroup": "Nav grupas",
    "access.users.empty": "Vēl nav lietotāju.",
    "access.users.emptyHint": "Izveidojiet pirmo lietotāju, lai piešķirtu piekļuvi.",
    "access.users.field.name": "Pilns vārds",
    "access.users.field.namePlaceholder": "piem., I. Liepa",
    "access.users.field.username": "Lietotājvārds",
    "access.users.field.usernamePlaceholder": "lietotājvārds",
    "access.users.field.email": "E-pasts",
    "access.users.field.lang": "Valoda",
    "access.users.field.department": "Departaments",
    "access.users.field.groups": "Atļauju grupas",
    "access.users.field.groupsHint": "Lietotājs manto visatļautāko līmeni no visām savām grupām.",
    "access.users.noDept": "Nav departamenta",
    "access.users.create": "Izveidot lietotāju",
    "access.users.save": "Saglabāt izmaiņas",
    "access.users.created": "Lietotājs izveidots: {name}",
    "access.users.updated": "Lietotājs atjaunināts: {name}",
    "access.users.saveErr": "Nevarēja saglabāt — pārbaudiet tiesības.",
    "access.users.createErr": "Nevarēja izveidot lietotāju.",
    "access.users.activated": "{name} aktivizēts",
    "access.users.deactivated": "{name} deaktivizēts",
    "access.users.deactivateTitle": "Deaktivizēt lietotāju",
    "access.users.deactivateConfirm":
      "Deaktivizēt “{name}”? Konts un vēsture saglabājas, bet vairs nevar pieslēgties vai saņemt jaunus uzdevumus.",
    "access.users.toggleErr": "Nevarēja atjaunināt lietotāju.",
    "access.users.passwordNote":
      "Sākotnējās paroles lietotājs iestata caur servera izsniegtu atiestatīšanu — šī forma nekad nesūta atklātu paroli.",
    "access.perms.group": "Atļauju grupa",
    "access.perms.save": "Saglabāt izmaiņas",
    "access.perms.saveTitle": "Saglabāt atļauju izmaiņas",
    "access.perms.saveConfirm": "Piemērot rediģētos piekļuves līmeņus grupai “{name}”? Tas nekavējoties ietekmē visus dalībniekus.",
    "access.perms.saved": "Saglabātas atļaujas grupai {name}",
    "access.perms.saveErr": "Nevarēja saglabāt — pārbaudiet tiesības.",
    "access.perms.empty": "Nav atļauju grupu.",
    "access.perms.emptyHint": "Vispirms izveidojiet grupu sadaļā Lietotāju grupas (RBAC).",
    "access.perms.menuVisible": "Redzams navigācijā",
    "access.perms.menuVisibleFor": "Izvēlnes redzamība modulim {module}",
    "access.perms.levelFor": "{module} piekļuves līmenis",
    "access.perms.note":
      "Piekļuves līmeņi attiecas uz visiem grupas dalībniekiem. Skatīt = tikai lasīšana, Rediģēt = izveide un labošana, Pilna = ieskaitot dzēšanu un konfigurāciju.",
    "access.perms.defaultsNote": "Rāda sākotnējos līmeņus. Izvēlieties līmeni katram modulim un saglabājiet.",
    "access.companies.title": "Uzņēmumi",
    "access.companies.add": "Pievienot uzņēmumu",
    "access.companies.new": "Jauns uzņēmums",
    "access.companies.edit": "Rediģēt uzņēmumu",
    "access.companies.tenant": "viens nomnieks",
    "access.companies.col.name": "Uzņēmums",
    "access.companies.col.currency": "Valūta",
    "access.companies.col.active": "Aktīvs",
    "access.companies.empty": "Vēl nav uzņēmumu.",
    "access.companies.emptyHint": "Pievienojiet uzņēmumu, lai piesaistītu lietotājus un iekārtas.",
    "access.companies.field.name": "Uzņēmuma nosaukums",
    "access.companies.field.namePlaceholder": "piem., A/S ITERUM",
    "access.companies.field.currency": "Valūta",
    "access.companies.field.currencyPlaceholder": "piem., EUR",
    "access.companies.create": "Izveidot uzņēmumu",
    "access.companies.save": "Saglabāt izmaiņas",
    "access.companies.created": "Uzņēmums pievienots: {name}",
    "access.companies.updated": "Uzņēmums atjaunināts: {name}",
    "access.companies.saveErr": "Nevarēja saglabāt uzņēmumu.",
    "access.depts.title": "Departamenti",
    "access.depts.add": "Pievienot departamentu",
    "access.depts.new": "Jauns departaments",
    "access.depts.edit": "Rediģēt departamentu",
    "access.depts.hint": "Apakšdepartamenti manto no sava vecāka.",
    "access.depts.empty": "Vēl nav departamentu.",
    "access.depts.emptyHint": "Pievienojiet pirmo nodaļu, lai organizētu iekārtas un cilvēkus.",
    "access.depts.field.name": "Departamenta nosaukums",
    "access.depts.field.namePlaceholder": "piem., Mehāniskā nodaļa",
    "access.depts.field.parent": "Vecākais departaments",
    "access.depts.noParent": "Nav vecāka (augšējais līmenis)",
    "access.depts.create": "Izveidot departamentu",
    "access.depts.save": "Saglabāt izmaiņas",
    "access.depts.created": "Departaments pievienots: {name}",
    "access.depts.updated": "Departaments atjaunināts: {name}",
    "access.depts.saveErr": "Nevarēja saglabāt departamentu.",
    "access.depts.edit.btn": "Rediģēt",
    "access.depts.delete": "Dzēst",
    "access.depts.deleteTitle": "Dzēst departamentu",
    "access.depts.deleteConfirm":
      "Dzēst “{name}”? Apakšdepartamenti pāriet augšējā līmenī; iekārtas saglabā ierakstus.",
    "access.depts.deleted": "Departaments dzēsts: {name}",
    "access.depts.deleteErr": "Nevarēja dzēst departamentu.",
    "tree.title": "Atrašanās vietu un iekārtu koks",
    "workboard.title": "Darbu panelis",
    "workboard.col.wo": "Darba uzdevums",
    "workboard.col.device": "Iekārta",
    "workboard.col.status": "Statuss",
    "workboard.col.due": "Termiņš · nokavējums",
    "workboard.col.priority": "Prioritāte",
    "workboard.filter.all": "Visi",
    "workboard.filter.open": "Atvērti",
    "workboard.filter.in_progress": "Procesā",
    "workboard.filter.completed": "Pabeigti",
    "workboard.empty": "Nekas neatbilst šim filtram.",
    "workboard.sortBy": "Kārtot pēc",
    "workboard.bulk.title": "Masveida darbības",
    "workboard.bulk.selected": "Atlasīti: {n}",
    "workboard.bulk.selectAll": "Atlasīt visus darba uzdevumus",
    "workboard.bulk.selectRow": "Atlasīt darba uzdevumu {wo}",
    "workboard.bulk.reassign": "Pārpiešķirt…",
    "workboard.bulk.reassignTitle": "Pārpiešķirt izpildītāju",
    "workboard.bulk.reassignFrom": "Pašreizējais izpildītājs: {from}",
    "workboard.bulk.newPerformer": "Jaunais izpildītājs",
    "workboard.bulk.selectUser": "Izvēlieties…",
    "workboard.bulk.reassignNeedsOne": "Atlasiet rindas ar vienu kopīgu izpildītāju, lai pārpiešķirtu.",
    "workboard.bulk.reassignScopeNote":
      "Pārpiešķiršana notiek pēc izpildītāja: VISI šī izpildītāja gaidāmie uzdevumi pāriet jaunajam izpildītājam (ne tikai atlasītās rindas). Pabeigtie uzdevumi netiek mainīti.",
    "workboard.bulk.reassignConfirmTitle": "Apstiprināt pārpiešķiršanu",
    "workboard.bulk.reassignConfirm": "Pārpiešķirt {n} gaidāmos uzdevumus no {from} uz {to}?",
    "workboard.bulk.reassigned": "Pārpiešķirti {n} uzdevumi",
    "workboard.bulk.reschedule": "Pārplānot…",
    "workboard.bulk.rescheduleTitle": "Pārplānot darba uzdevumus",
    "workboard.bulk.rescheduleDate": "Jaunais izpildes datums",
    "workboard.bulk.rescheduleAvoidWeekend": "Izvairīties no nedēļas nogalēm (Se./Sv. → piektdiena)",
    "workboard.bulk.rescheduleConfirmTitle": "Apstiprināt pārplānošanu",
    "workboard.bulk.rescheduleConfirm": "Pārcelt {n} atlasītos darba uzdevumus uz {date}?",
    "workboard.bulk.rescheduled": "Pārplānoti {n} darba uzdevumi",
    "workboard.bulk.rescheduleNone": "Nevienu no atlasītajiem uzdevumiem nevar pārplānot (pabeigtie/atceltie tiek izlaisti).",
    "workboard.bulk.clear": "Notīrīt atlasi",
    // ---- WAVE 3: darba uzdevuma detaļas ----
    "wo.details": "Detaļas",
    "wo.device": "Iekārta",
    "wo.service": "Pakalpojums",
    "wo.priority": "Prioritāte",
    "wo.due": "Termiņš",
    "wo.performer": "Izpildītājs",
    "wo.unassigned": "Nepiešķirts",
    "wo.noDevice": "Iekārta",
    "wo.noLocation": "—",
    "wo.checklist": "Kontrolsaraksts",
    "wo.checklist.empty": "Vēl nav kontrolsaraksta soļu.",
    "wo.checklist.add": "Pievienot soli",
    "wo.checklist.addPlaceholder": "Pievienot kontrolsaraksta soli…",
    "wo.checklist.delete": "Dzēst soli “{label}”",
    "wo.notes": "Piezīmes",
    "wo.notes.placeholder": "Servisa piezīmes — kas izdarīts, izmantotās daļas, novērojumi…",
    "wo.notes.save": "Saglabāt piezīmes",
    "wo.notesSaved": "Piezīmes saglabātas",
    "wo.timer.label": "Uzdevuma taimeris",
    "wo.timer.running": "Darbojas",
    "wo.timer.start": "Sākt",
    "wo.timer.stop": "Apturēt",
    "wo.timer.total": "Kopā reģistrēts",
    "wo.timer.entries": "Reģistrētie darba laika ieraksti",
    "wo.timer.elapsed": "Pagājušais laiks {time}",
    "wo.timerStarted": "Taimeris ieslēgts",
    "wo.timerStopped": "Taimeris apturēts",
    "wo.timerAlreadyRunning": "Jums jau darbojas taimeris šim darba uzdevumam.",
    "wo.signOff": "Pabeigt un parakstīt",
    "wo.block.loto": "Bloķēts: vispirms apstipriniet bloķēšanu/marķēšanu (LOTO).",
    "wo.block.noChecklist": "Bloķēts: pievienojiet vismaz vienu kontrolsaraksta soli.",
    "wo.block.checklist": "Bloķēts: jāizpilda visi kontrolsaraksta soļi.",
    "wo.block.signed": "Bloķēts: darba uzdevums jāparaksta.",
    // ---- WAVE 4 ----
    "register.title": "Uzdevumu reģistrs",
    "register.sub":
      "Pilns, stratēģisks visu darba uzdevumu reģistrs — lapots pa visu uzdevumu kopu. Kārtojiet, filtrējiet un eksportējiet; atveriet jebkuru rindu detaļām.",
    "register.search": "Meklēt numuru vai nosaukumu",
    "register.filter.status": "Statusa filtrs",
    "register.filter.priority": "Prioritātes filtrs",
    "register.filter.allStatus": "Visi statusi",
    "register.filter.allPriority": "Visas prioritātes",
    "register.filter.overdue": "Tikai nokavētie",
    "register.status.paused": "Apturēts",
    "register.status.cancelled": "Atcelts",
    "register.status.postponed": "Atlikts",
    "register.prio.critical": "Kritiska",
    "register.prio.high": "Augsta",
    "register.prio.normal": "Normāla",
    "register.prio.low": "Zema",
    "register.col.wo": "DU №",
    "register.col.title": "Nosaukums",
    "register.col.device": "Iekārta",
    "register.col.status": "Statuss",
    "register.col.priority": "Prioritāte",
    "register.col.assignee": "Izpildītājs",
    "register.col.due": "Termiņš",
    "register.col.overdue": "Nokavējums",
    "register.unassigned": "Nepiešķirts",
    "register.overdueFlag": "nokavēts",
    "register.overdueDays": "+{n}d",
    "register.overdueTitle": "Pārsniegts termiņš",
    "register.selectAll": "Atlasīt visas rindas šajā lapā",
    "register.selectRow": "Atlasīt darba uzdevumu {wo}",
    "register.export.all": "Eksportēt CSV",
    "register.export.selected": "Eksportēt CSV ({n})",
    "register.empty": "Nav darba uzdevumu.",
    "register.emptyHint": "Nekas neatbilst pašreizējiem filtriem šajā lapā.",
    "register.range.of": "{from}–{to} no {total}",
    "register.range.window": "Rāda {from}–{to}",
    "register.range.none": "Nav rindu",
    "register.prev": "Iepriekšējā",
    "register.next": "Nākamā",
    "status.open": "Atvērts",
    "status.in_progress": "Procesā",
    "status.paused": "Apturēts",
    "status.completed": "Pabeigts",
    "status.cancelled": "Atcelts",
    "status.postponed": "Atlikts",
    "common.unknownUser": "(nezināms)",
    "scheduler.service": "Apkope",
    "scheduler.priority": "Prioritāte",
    "scheduler.close": "Aizvērt",
    "downtimes.uncategorized": "(bez kategorijas)",
    "executors.title": "Izpildītāji un komandas",
    "executors.sub":
      "Izpildītāji ir lietotāji. Aktivizējiet vai deaktivizējiet tos, rediģējiet datus un pievienojiet jaunus. Deaktivizētie izpildītāji izkrīt no jaunu uzdevumu izvēlēm, bet paliek visos vēsturiskajos ierakstos.",
    "executors.add": "Pievienot izpildītāju",
    "executors.create": "Izveidot izpildītāju",
    "executors.save": "Saglabāt izpildītāju",
    "executors.field.name": "Attēlojamais vārds",
    "executors.field.namePlaceholder": "Pilns vārds",
    "executors.field.username": "Lietotājvārds",
    "executors.field.email": "E-pasts",
    "executors.field.group": "Grupa / loma",
    "executors.noGroup": "Nav grupas",
    "executors.active": "Aktīvs",
    "executors.inactive": "Neaktīvs",
    "executors.activePerformer": "Aktīvs izpildītājs",
    "executors.activeHint":
      "Kad izslēgts, {name} pazūd no jaunu uzdevumu piešķiršanas izvēlēm — bet katrs pabeigtais darba uzdevums saglabā viņa vārdu auditam.",
    "executors.empty": "Vēl nav izpildītāju.",
    "executors.selectHint": "Atlasiet izpildītāju, lai skatītu un rediģētu datus.",
    "executors.saved": "Saglabāts {name}",
    "executors.saveErr": "Nevarēja saglabāt — pārbaudiet tiesības.",
    "executors.created": "Izpildītājs izveidots: {name}",
    "executors.createErr": "Nevarēja izveidot izpildītāju.",
    "executors.activated": "{name} aktivizēts",
    "executors.deactivated": "{name} deaktivizēts",
    "groups.title": "Lietotāju grupas un lomas (RBAC)",
    "groups.sub":
      "Uz lomām balstītas piekļuves grupas. Izveidojiet, pārdēvējiet un dzēsiet grupas šeit; pilna lasīšanas/rakstīšanas/dzēšanas matrica tiek rediģēta sadaļā Cilvēki un piekļuve.",
    "groups.groups": "Grupas",
    "groups.new": "Jauna grupa",
    "groups.newTitle": "Jauna atļauju grupa",
    "groups.editTitle": "Rediģēt grupu",
    "groups.field.name": "Grupas nosaukums",
    "groups.field.namePlaceholder": "piem., Nakts maiņas tehniķis",
    "groups.matrixNote": "Lasīšanas/rakstīšanas/dzēšanas matrica tiek konfigurēta sadaļā Cilvēki un piekļuve.",
    "groups.members": "{n} lietotāji",
    "groups.noPerms": "Vēl nav piešķirtu atļauju.",
    "groups.edit": "Rediģēt",
    "groups.delete": "Dzēst",
    "groups.deleteTitle": "Dzēst grupu",
    "groups.deleteConfirm": "Dzēst grupu “{name}”? Lietotāji saglabā kontus, bet zaudē šīs grupas piekļuvi.",
    "groups.create": "Izveidot grupu",
    "groups.save": "Saglabāt izmaiņas",
    "groups.created": "Grupa izveidota: {name}",
    "groups.updated": "Grupa atjaunināta: {name}",
    "groups.deleted": "Grupa dzēsta: {name}",
    "groups.saveErr": "Nevarēja saglabāt grupu.",
    "groups.deleteErr": "Nevarēja dzēst grupu.",
    "groups.empty": "Nav atļauju grupu.",
    "groups.emptyHint": "Izveidojiet pirmo grupu, lai sāktu piešķirt piekļuvi.",
    "common.close": "Aizvērt",
    "nav.collapse": "Sakļaut",
    "nav.expand": "Izvērst sānjoslu",
    "nav.autoCollapsed": "Automātiski sakļauts (šaurs)",
    "profile.myProfile": "Mans profils",
    "profile.preferences": "Iestatījumi",
    "profile.toggleTheme": "Mainīt tēmu",
    "profile.group": "Grupa",
    "profile.company": "Uzņēmums",
    "profile.username": "Lietotājvārds",
    "profile.email": "E-pasts",
    "profile.language": "Valoda",
    "settings.nav.title": "Navigācija",
    "settings.nav.layout": "Navigācijas izkārtojums",
    "settings.nav.layoutHint":
      "Izvēlieties, kur atrodas izvēlne. Sānu josla der blīvam plānošanas darbam; augšējā josla atbrīvo vertikālo telpu tabulām un shēmām.",
    "settings.nav.side": "Sānu josla",
    "settings.nav.top": "Augšējā josla",
    "settings.nav.width": "Sānjoslas platums",
    "settings.nav.widthHint":
      "Izvērsts rāda nosaukumus; sakļauts ir ikonu josla ar uzvednēm. Automātiski sakļaujas šauros logos.",
    "settings.nav.expanded": "Izvērsts",
    "settings.nav.collapsed": "Sakļauts",
    "downtimes.title": "Dīkstāves reģistrs",
    "downtimes.kpi.total": "Kopējā dīkstāve",
    "downtimes.kpi.events": "Reģistrētie notikumi",
    "downtimes.kpi.down": "Šobrīd dīkstāvē",
    "downtimes.kpi.mtbf": "MTBF · vidējais laiks starp atteicēm",
    "downtimes.kpi.availability": "Pieejamība",
    "downtimes.kpi.na": "nav",
    "downtimes.kpi.naHint": "Nepieciešama vismaz viena slēgta dīkstāve.",
    "downtimes.byCause": "Pēc cēloņa",
    "downtimes.empty": "Dīkstāve vēl nav reģistrēta.",
    "downtimes.emptyHint": "Reģistrējiet pirmo dīkstāves periodu, lai sāktu aprēķināt pieejamību un MTBF.",
    "downtimes.noCats": "Nav definētu dīkstāves kategoriju.",
    "downtimes.col.actions": "Darbības",
    "downtimes.convert": "Pārvērst par darba uzdevumu",
    "downtimes.convertTitle": "Pārvērst dīkstāvi par darba uzdevumu?",
    "downtimes.convertBody": "Izveidot jaunu darba uzdevumu no šī bojājuma ({asset}). Tas tiks atvērts Darba tāfelē.",
    "downtimes.convertConfirm": "Izveidot darba uzdevumu",
    "downtimes.converted": "Izveidots darba uzdevums {number}",
    "downtimes.convertErr": "Neizdevās pārvērst dīkstāvi par darba uzdevumu.",
    "downtimes.convertNoPerm": "Jums nav tiesību veidot darba uzdevumus.",
    "downtimes.ongoing": "turpinās",
    "downtimes.statusLabel": "Statuss",
    "downtimes.resolved": "Atrisināts",
    "inventory.title": "Rezerves daļu noliktava",
    "inventory.available": "Pieejams",
    "inventory.booked": "Rezervēts",
    "inventory.inStock": "Noliktavā",
    "inventory.noSpares": "Rezerves daļu vēl nav.",
    "inventory.noSparesHint": "Pievienojiet rezerves daļas vārdnīcās, lai sāktu rezervēt, saņemt un pārvietot krājumus.",
    "reports.title": "Atskaites",
    "reports.empty": "Nav rindu šiem filtriem.",
    "reports.emptyHint": "Paplašiniet datumu diapazonu vai izvēlieties citu atskaiti.",
    "reports.col.deviceId": "Iekārta",
    "reports.col.userId": "Tehniķis",
    "reports.col.taskId": "Darba uzdevums",
    "reports.col.failures": "Atteices",
    "reports.col.downtimeHours": "Dīkstāve",
    "reports.col.repairHours": "Remonta laiks",
    "reports.col.workHours": "Darba laiks",
    "reports.col.startedAt": "Sākts",
    "reports.col.endedAt": "Beigts",
    "reports.col.reason": "Iemesls",
    "reports.col.spare": "Detaļa",
    "reports.col.name": "Nosaukums",
    "reports.col.movementType": "Kustība",
    "reports.col.quantity": "Daudz.",
    "reports.col.lineValue": "Vērtība",
    "reports.col.docDate": "Datums",
    "reports.col.number": "Nr.",
    "reports.col.title": "Nosaukums",
    "reports.col.status": "Statuss",
    "reports.col.priority": "Prioritāte",
    "reports.col.completedAt": "Pabeigts",
    "nav.dashboard": "Atskaites un panelis",
    "dashboard.title": "Atskaites un panelis",
    "dashboard.sub":
      "Rūpnīcas uzturēšanas veselība vienā skatā — pieejamība, MTBF, grafika izpilde un rezerves daļu izdevumi, aprēķināti no reālajām atskaitēm. Neaprēķināmie rādītāji rāda “nav”, nekad neizdomātus skaitļus.",
    "dashboard.na": "nav",
    "dashboard.naHint": "Pagaidām nav aprēķināms no pieejamajiem atskaišu datiem.",
    "dashboard.kpi.uptime": "Iekārtu pieejamība",
    "dashboard.kpi.mtbf": "MTBF · vidējais laiks starp atteicēm",
    "dashboard.kpi.completion": "Pabeigtie darba uzdevumi",
    "dashboard.kpi.partsSpend": "Rezerves daļu izdevumi (iepirkumi)",
    "dashboard.repairs": "Remonti — plānoti pret neplānotiem",
    "dashboard.planned": "Plānoti",
    "dashboard.unplanned": "Neplānoti",
    "dashboard.noRepairs": "Vēl nav pabeigtu darba uzdevumu diagrammai.",
    "dashboard.downtimeByLine": "Dīkstāve pa iekārtām",
    "dashboard.totalDowntime": "kopā",
    "dashboard.noDowntime": "Dīkstāve vēl nav reģistrēta.",
    "dashboard.topAssets": "Iekārtas ar lielāko uzturēšanas slodzi",
    "dashboard.topAssetsHint": "pēc kopējām dīkstāves stundām",
    "dashboard.noAssets": "Vēl nav iekārtu dīkstāves, ko sarindot.",
    "dashboard.assetMeta": "{failures} atteice(s)",
    "dashboard.assetLoad": "{hours} h dīkstāve",
    "common.loading": "Ielādē…",
    "nav.review": "Pārskata centrs",
    "review.title": "Pārskata centrs",
    "chrome.search": "Meklēt",
    "chrome.offline": "Bezsaistē (kešatmiņā)",
    "chrome.pending": "gaida sinhronizāciju",
    // ---- WAVE 7: mērījumu veidnes + rādījumi ----
    "nav.measTemplates": "Mērījumu veidnes",
    "nav.meters": "Skaitītāju rādījumi",
    "measTpl.title": "Mērījumu veidnes",
    "measTpl.sub": "Definējiet LL/L/H/HH pielaides joslas katram mērpunktam, sagrupētas veidnēs, kas piesaistītas iekārtu kategorijām.",
    "measTpl.list": "Veidnes",
    "measTpl.add": "Jauna veidne",
    "measTpl.addTitle": "Jauna mērījumu veidne",
    "measTpl.name": "Veidnes nosaukums",
    "measTpl.namePlaceholder": "piem., Ekstrudera vibrācija",
    "measTpl.category": "Iekārtas kategorija",
    "measTpl.categoryNone": "— (nepiesaistīta)",
    "measTpl.empty": "Vēl nav mērījumu veidņu",
    "measTpl.emptyHint": "Izveidojiet veidni, pēc tam pievienojiet mērpunktus ar pielaides joslām.",
    "measTpl.rename": "Pārdēvēt veidni",
    "measTpl.deleteTitle": "Dzēst veidni?",
    "measTpl.deleteBody": "Tas noņem veidni un visus tās mērpunktus. Reģistrētie rādījumi tiek saglabāti.",
    "measTpl.selectHint": "Atlasiet veidni, lai rediģētu tās punktus",
    "measTpl.points": "Mērpunkti",
    "measTpl.pointAdd": "Pievienot punktu",
    "measTpl.pointName": "Punkta nosaukums",
    "measTpl.pointNamePlaceholder": "piem., Gultnis DE",
    "measTpl.unit": "Mērvienība",
    "measTpl.unitPlaceholder": "piem., mm/s",
    "measTpl.pointsEmpty": "Šai veidnei vēl nav punktu",
    "measTpl.pointsEmptyHint": "Pievienojiet punktu ar tā LL/L/H/HH sliekšņiem; priekšskatījuma josla parāda joslas.",
    "measTpl.pointEdit": "Rediģēt punktu",
    "measTpl.pointDeleteTitle": "Dzēst punktu?",
    "measTpl.pointDeleteBody": "Tas noņem mērpunktu un tā pielaides joslas.",
    "measTpl.preview": "Joslu priekšskatījums",
    "measTpl.thresholdOrder": "Sliekšņiem jāatbilst LL ≤ L ≤ H ≤ HH.",
    "measTpl.col.point": "Punkts",
    "measTpl.col.unit": "Mērv.",
    "measTpl.col.bands": "LL / L / H / HH",
    "meters.title": "Skaitītāju rādījumi",
    "meters.sub": "Izvēlieties iekārtu, pēc tam reģistrējiet rādījumu katram tās mērpunktam. Katra vērtība tiek pārbaudīta pret pielaides joslu reāllaikā.",
    "meters.device": "Iekārta",
    "meters.devicePlaceholder": "Atlasiet iekārtu…",
    "meters.noDevice": "Izvēlieties iekārtu, lai reģistrētu rādījumus",
    "meters.noDeviceHint": "Šeit ielādējas tās mērpunkti (no piesaistītās kategorijas veidnes).",
    "meters.noPoints": "Šai iekārtai nav mērpunktu",
    "meters.noPointsHint": "Piesaistiet iekārtas kategoriju mērījumu veidnei ar punktiem un atgriezieties.",
    "meters.point": "Punkts",
    "meters.latest": "Pēdējais",
    "meters.newValue": "Jauns rādījums",
    "meters.record": "Reģistrēt",
    "meters.recorded": "Rādījums reģistrēts",
    "meters.inTolerance": "Pielaidē",
    "meters.warn": "Ārpus brīdinājuma joslas",
    "meters.alarm": "Ārpus trauksmes joslas — pārbaudiet",
    "meters.noReading": "Vēl nav rādījuma",
    "meters.recent": "Nesenie rādījumi",
    "meters.recentEmpty": "Vēl nav reģistrētu rādījumu",
    "meters.noPerm": "Jums nav atļaujas reģistrēt rādījumus.",
    // ---- admin console ----
    "admin.title": "Administrēšanas konsole",
    "admin.sub": "Masveida darbības, līdzekļu pārslēgi katram nomniekam, drošība (IP atļaušana/bloķēšana + audita pieraksti) un Dynamics 365 Business Central integrācijas centrs.",
    "admin.tabsLabel": "Administrēšanas cilnes",
    "admin.tab.flags": "Līdzekļu pārslēgi",
    "admin.tab.mass": "Masveida nomaiņa",
    "admin.tab.security": "Drošība",
    "admin.tab.bc": "Dynamics 365 BC",
    "admin.flag.enabled": "{name} ieslēgts",
    "admin.flag.disabled": "{name} izslēgts",
    "admin.flag.toggleErr": "Neizdevās pārslēgt — pārbaudiet savas atļaujas.",
    "admin.mass.step.current": "Pašreizējais izpildītājs",
    "admin.mass.step.scope": "Tvērums",
    "admin.mass.step.new": "Jaunais izpildītājs",
    "admin.mass.step.confirm": "Apstiprināt",
    "admin.mass.select": "Izvēlieties…",
    "admin.mass.filterLoc": "Filtrēt pēc atrašanās vietas (neobligāti)",
    "admin.mass.allLocations": "Visas atrašanās vietas",
    "admin.mass.reassigned": "Pārpiešķirti {n} uzdevumi",
    "admin.mass.reassignErr": "Neizdevās pārpiešķirt — pārbaudiet savas atļaujas.",
    "admin.mass.confirmLead": "Pārpiešķirt",
    "admin.mass.confirmTrail": "gaidāmos uzdevumus jaunajam izpildītājam. Pabeigtie uzdevumi netiek skarti.",
    "admin.mass.back": "Atpakaļ",
    "admin.mass.continue": "Turpināt",
    "admin.mass.transfer": "Pārnest {n} uzdevumus",
    "admin.sec.whitelisted": "Atļauts {cidr}",
    "admin.sec.invalidCidr": "Nederīgs CIDR / IP.",
    "admin.sec.addErr": "Neizdevās pievienot diapazonu.",
    "admin.sec.unblockTitle": "Atbloķēt {ip}?",
    "admin.sec.unblockBody": "Šai IP atkal būs atļauts autentificēties.",
    "admin.sec.unblockConfirm": "Atbloķēt IP",
    "admin.sec.unblocked": "Atbloķēts {ip}",
    "admin.sec.unblockErr": "Neizdevās atbloķēt.",
    "admin.sec.testInvalidIp": "Ievadiet vienu IPv4 adresi (bez CIDR).",
    "admin.sec.testErr": "Neizdevās pārbaudīt IP.",
    "admin.sec.exportErr": "Neizdevās eksportēt — pārbaudiet savas atļaujas.",
    "admin.sec.blocklist": "Aktīvais bloķēšanas saraksts",
    "admin.sec.blockedCount": "{n} bloķēti",
    "admin.sec.colIp": "IP adrese",
    "admin.sec.colReason": "Iemesls",
    "admin.sec.unblock": "Atbloķēt",
    "admin.sec.noBlocked": "Nav bloķētu adrešu",
    "admin.sec.testTitle": "Pārbaudīt IP",
    "admin.sec.testHint": "pārbaudīt atļaušanu / bloķēšanu pret pašreizējiem noteikumiem",
    "admin.sec.testInputLabel": "Pārbaudāmā IP adrese",
    "admin.sec.testPlaceholder": "piem. 10.4.22.187",
    "admin.sec.test": "Pārbaudīt",
    "admin.sec.wouldBe": "tiktu",
    "admin.sec.allowed": "ATĻAUTA",
    "admin.sec.blocked": "BLOĶĒTA",
    "admin.sec.reasonAllowlisted": "(atbrīvota ar atļaušanas diapazonu {ranges})",
    "admin.sec.reasonBlocked": "(atbilst bloķēšanas noteikumam {rules})",
    "admin.sec.reasonDefaultAllow": "(neviens noteikums neatbilst — atļauts pēc noklusējuma)",
    "admin.sec.allowedRanges": "Atļautie diapazoni (CIDR)",
    "admin.sec.removeRange": "Noņemt {cidr}",
    "admin.sec.newRangeLabel": "Jauns CIDR diapazons",
    "admin.sec.cidrPlaceholder": "piem. 172.16.0.0/12",
    "admin.sec.addRange": "Pievienot diapazonu",
    "admin.sec.auditTrail": "Audita pieraksti",
    "admin.sec.exportCsv": "Eksportēt CSV",
    "admin.sec.filterFrom": "No",
    "admin.sec.filterTo": "Līdz",
    "admin.sec.filterActor": "Izpildītāja ID",
    "admin.sec.filterActorPlaceholder": "lietotāja / API atslēgas ID",
    "admin.sec.filterAction": "Darbība",
    "admin.sec.filterActionPlaceholder": "piem. Bloķēts",
    "admin.sec.noDiff": "Vecās → jaunās vērtības detaļas audita pierakstos netiek reģistrētas; tālāk redzamās kolonnas ir viss fiksētais.",
    "admin.sec.colTime": "Laiks",
    "admin.sec.colActor": "Izpildītājs",
    "admin.sec.colTarget": "Mērķis",
    "admin.sec.colIpShort": "IP",
    "admin.sec.colAction": "Darbība",
    "admin.sec.tagMobile": "mobilais",
    "admin.sec.noAudit": "Pašreizējiem filtriem neatbilst neviens audita ieraksts.",
    "admin.bc.title": "Vienumu sinhronizācija · Dynamics 365 BC",
    "admin.bc.note": "BC produkta ID ir tikai lasāms — mēs tam piesaistāmies, nekad to nepārdēvējam. Atjaunojot īpašnieka API atslēgu, integrācija tiek pārtraukta, tāpēc tās piesaistes tiek aprēķinātas kā Bāreņi.",
    "admin.bc.colSpare": "Rezerves daļa",
    "admin.bc.colProductId": "Dynamics produkta ID",
    "admin.bc.colStatus": "Statuss",
    "admin.bc.state.synced": "Sinhronizēts",
    "admin.bc.state.out_of_sync": "Nav sinhronizēts",
    "admin.bc.state.orphaned": "Bārenis",
    "admin.bc.empty": "Vēl nav rezerves daļu↔vienumu piesaistes. Piesaistiet rezerves daļu BC vienumam, lai sāktu sinhronizāciju.",
    // ---- review center ----
    "review.sub": "Bezsaistes izmaiņas, kuras nevarēja piemērot automātiski — to mērķis tika dzēsts vai pārpiešķirts. Piemērojiet, lai uzspiestu, vai atmetiet.",
    "review.reason.target_deleted": "Mērķis dzēsts",
    "review.reason.target_reassigned": "Pārpiešķirts citam",
    "review.tab.open": "Atvērtie",
    "review.tab.resolved": "Atrisinātie",
    "review.emptyOpen": "Nav novietotu izmaiņu — viss sinhronizēts tīri.",
    "review.emptyResolved": "Vēl nav atrisinātu vienumu.",
    "review.applyTitle": "Piemērot novietoto izmaiņu?",
    "review.discardTitle": "Atmest novietoto izmaiņu?",
    "review.applyBody": "Piespiedu kārtā piemērot šo {op} uz {table}. Tas ignorē dzēšanu/pārpiešķiršanu, kas to novietoja.",
    "review.discardBody": "Atmest šo {op} uz {table}. Novietotā izmaiņa tiks atmesta.",
    "review.applyConfirm": "Piemērot",
    "review.discardConfirm": "Atmest",
    "review.applied": "Izmaiņa piemērota",
    "review.discarded": "Izmaiņa atmesta",
    "review.notPermitted": "Nav atļauts (nepieciešama uzdevumu pilnā piekļuve)",
    "review.resolveErr": "Neizdevās atrisināt ({code})",
    "review.colChange": "Izmaiņa",
    "review.colWas": "Bija",
    "review.apply": "Piemērot",
    "review.discard": "Atmest",
    "review.resolved": "atrisināts",
    // ---- device card ----
    "dc.close": "Aizvērt",
    "dc.nfc": "NFC",
    "dc.sectionsLabel": "Iekārtas kartes sadaļas",
    "dc.status.operational": "Darbībā",
    "dc.status.maintenance": "Apkope",
    "dc.status.down": "Dīkstāvē",
    "dc.status.decommissioned": "Izņemts no ekspluatācijas",
    "dc.noOpenWos": "Nav atvērtu darba uzdevumu.",
    "dc.maint.intervals": "Apkopes intervāli",
    "dc.maint.kind.counter": "Skaitītājs",
    "dc.maint.kind.time": "Laiks",
    "dc.maint.onSchedule": "pēc grafika",
    "dc.maint.none": "Nav ieplānotu apkopes intervālu.",
    "dc.measure.points": "Mērījumu punkti",
    "dc.measure.noTemplate": "Šai iekārtai nav piesaistīta mērījumu veidne.",
    "dc.measure.normal": "normāls",
    "dc.downtime.log": "Dīkstāvju žurnāls",
    "dc.downtime.default": "Dīkstāve",
    "dc.downtime.ongoing": "turpinās",
    "dc.downtime.min": "{n} min",
    "dc.files.documents": "Dokumenti",
    "dc.files.colFile": "Fails",
    "dc.files.colType": "Tips",
    "dc.files.colAdded": "Pievienots",
    "dc.files.namePlaceholder": "instrukcija.pdf",
    "dc.files.urlPlaceholder": "https://… vai data:…",
    "dc.history.timeline": "Vienotā laika līnija",
    "dc.spares.mapped": "Piesaistītās rezerves daļas",
    "dc.spares.hint": "Pieejams = Noliktavā − Rezervēts (rezervēts atvērtajos darba uzdevumos)",
    "dc.spares.colCode": "Kods",
    "dc.spares.colPart": "Daļa",
    "dc.spares.colInStock": "Noliktavā",
    "dc.spares.colBooked": "Rezervēts",
    "dc.spares.colAvailable": "Pieejams",
    "dc.spares.colMin": "Min.",
    "dc.spares.colStatus": "Statuss",
    "dc.spares.belowMin": "Zem minimuma",
    "dc.spares.ok": "Labi",
    "dc.spares.none": "Šai iekārtai nav piesaistītu rezerves daļu.",
    // ---- command palette / notifications ----
    "cmdk.label": "Komandu palete",
    "cmdk.placeholder": "Meklēt aktīvus, darba uzdevumus, rezerves daļas, cilvēkus…",
    "cmdk.searchLabel": "Meklēt",
    "cmdk.resultsLabel": "Meklēšanas rezultāti",
    "cmdk.hint": "Rakstiet, lai meklētu visā rūpnīcā.",
    "cmdk.esc": "Esc",
    "cmdk.noMatches": "Nav atbilstību vaicājumam “{q}”.",
    "notif.label": "Paziņojumi",
    "notif.unread": "nelasīts",
    "notif.bell": "Paziņojumi",
    "notif.bellUnread": "Paziņojumi ({n} nelasīti)",
    "notif.markAll": "Atzīmēt visus kā lasītus",
    "notif.empty": "Viss izskatīts.",
    "notif.ago.justNow": "tikko",
    "notif.ago.min": "pirms {n} min",
    "notif.ago.hour": "pirms {n} h",
    "notif.ago.day": "pirms {n} d",
  },
  ru: {
    "login.title": "Вход",
    "login.sub": "Консоль обслуживания завода",
    "login.username": "Имя пользователя",
    "login.password": "Пароль",
    "login.submit": "Войти",
    "login.mfaTitle": "Двухфакторная проверка",
    "login.mfaSub": "Введите 6-значный код из приложения-аутентификатора.",
    "login.mfaCode": "Код подтверждения",
    "login.mfaSubmit": "Подтвердить",
    "login.err.invalid_credentials": "Неверное имя пользователя или пароль.",
    "login.err.invalid_code": "Неверный код — попробуйте снова.",
    "settings.title": "Настройки и справочники",
    "settings.sub":
      "Здесь настраивается каждый выпадающий список системы — единый принцип управляемого списка, а не лабиринт отдельных таблиц. Категории оборудования также несут схему пользовательских полей (движок из 8 типов).",
    "settings.dict.railLabel": "Справочники",
    "settings.dict.devcat": "Категории оборудования",
    "settings.dict.devcatHint": "Типы активов + их схема пользовательских полей",
    "settings.dict.stocks": "Склады / запасы",
    "settings.dict.stocksHint": "Места хранения запасов",
    "settings.dict.downcat": "Категории простоев",
    "settings.dict.downcatHint": "Причины для журнала простоев",
    "settings.dict.taskstage": "Этапы задач",
    "settings.dict.taskstageHint": "Фиксированный набор статусов наряд-заказов",
    "settings.dict.devmfr": "Производители оборудования",
    "settings.dict.sparecat": "Категории запчастей",
    "settings.dict.sparemfr": "Производители запчастей",
    "settings.dict.taskgrp": "Группы задач",
    "settings.dict.reportstatus": "Статусы отчётов",
    "settings.dict.checklist": "Шаблоны чек-листов",
    "settings.dict.tag.readonly": "только чтение",
    "settings.dict.tag.soon": "скоро",
    "settings.dict.col.name": "Название",
    "settings.dict.col.value": "Значение",
    "settings.dict.col.main": "Основной",
    "settings.dict.yes": "да",
    "settings.dict.add": "Добавить",
    "settings.dict.edit": "Изменить",
    "settings.dict.delete": "Удалить",
    "settings.dict.editNamed": "Изменить «{name}»",
    "settings.dict.deleteNamed": "Удалить «{name}»",
    "settings.dict.create": "Создать",
    "settings.dict.save": "Сохранить изменения",
    "settings.dict.newTitle": "Новый: {group}",
    "settings.dict.editTitle": "Изменить: {group}",
    "settings.dict.namePlaceholder": "Новое название {group}…",
    "settings.dict.mainHint": "Основной склад (по умолчанию для приёмки)",
    "settings.dict.added": "Добавлено «{name}» в {group}",
    "settings.dict.updated": "Обновлено «{name}»",
    "settings.dict.deleted": "Удалено «{name}»",
    "settings.dict.deleteTitle": "Удалить «{name}»?",
    "settings.dict.deleteBody":
      "Это удаляет запись из справочника {group}. Записи, уже использующие её, сохраняют значение.",
    "settings.dict.writeErr": "Не удалось сохранить — проверьте права.",
    "settings.dict.deleteErr": "Не удалось удалить — проверьте права.",
    "settings.dict.empty": "Пока нет: {group}.",
    "settings.dict.emptyHint": "Добавьте первую запись, чтобы использовать её в списках.",
    "settings.dict.readonlyNote":
      "Этапы задач — это фиксированный, заданный сервером набор статусов; их нельзя редактировать. Показаны здесь для справки.",
    "settings.dict.unbacked.title": "Пока не настраивается",
    "settings.dict.unbacked.hint": "У этого справочника нет основной таблицы.",
    "settings.dict.unbacked.note":
      "У этого справочника сегодня нет отдельной таблицы арендатора в /api/v1. Производители — свободный текст у оборудования и запчастей; группы задач, статусы отчётов и шаблоны чек-листов пока не обеспечены. Это будет добавлено позже — здесь ничего не выдумывается.",
    "settings.cf.title": "Пользовательские поля",
    "settings.cf.addField": "Добавить поле",
    "settings.cf.newCatLabel": "Название новой категории оборудования",
    "settings.cf.newCatPlaceholder": "Новая категория оборудования, напр. Экструдер…",
    "settings.cf.noCats": "Пока нет категорий оборудования.",
    "settings.cf.noCatsHint": "Добавьте категорию, затем привяжите её пользовательские поля.",
    "settings.cf.deleteCatBody":
      "Это удаляет категорию и отвязывает её схему пользовательских полей. Оборудование сохраняет записанные значения.",
    "settings.cf.none": "У этой категории нет пользовательских полей.",
    "settings.cf.noneHint": "Добавьте поле, чтобы расширить карточку оборудования этого типа.",
    "settings.cf.newTitle": "Новое пользовательское поле",
    "settings.cf.editTitle": "Изменить пользовательское поле",
    "settings.cf.label": "Название поля",
    "settings.cf.labelPlaceholder": "напр., Зазор статора (мм)",
    "settings.cf.type": "Тип поля",
    "settings.cf.suffix": "Единица / суффикс",
    "settings.cf.suffixPlaceholder": "напр., мм, кВт, °C (необязательно)",
    "settings.cf.required": "Обязательное",
    "settings.cf.requiredHint": "Обязательно в карточке оборудования",
    "settings.cf.options": "Варианты выбора",
    "settings.cf.optionsEmpty": "Варианты не добавлены.",
    "settings.cf.optionValue": "Значение варианта",
    "settings.cf.optionPlaceholder": "Добавить значение варианта…",
    "settings.cf.addOption": "Добавить",
    "settings.cf.removeOption": "Удалить вариант «{value}»",
    "settings.cf.save": "Сохранить изменения",
    "settings.cf.added": "Добавлено поле «{name}»",
    "settings.cf.updated": "Обновлено поле «{name}»",
    "settings.cf.deleted": "Удалено поле «{name}»",
    "settings.cf.editNamed": "Изменить поле «{name}»",
    "settings.cf.deleteNamed": "Удалить поле «{name}»",
    "settings.cf.deleteTitle": "Удалить поле «{name}»?",
    "settings.cf.deleteBody":
      "Это удаляет поле из схемы категории. Сохранённые значения оборудования для этого поля не удаляются.",
    "settings.cf.writeErr": "Не удалось сохранить поле — проверьте права.",
    "access.title": "Люди и доступ",
    "access.sub":
      "Пользователи, группы разрешений, компании и подразделения в одном месте. Разрешения — это простые уровни доступа по модулю: чтение (Просмотр), создание и изменение (Изменение), включая удаление и настройку (Полный) — та же матрица, которую применяет сервер.",
    "access.tab.users": "Пользователи",
    "access.tab.perms": "Разрешения",
    "access.tab.companies": "Компании",
    "access.tab.depts": "Подразделения",
    "access.users.title": "Пользователи",
    "access.users.add": "Добавить пользователя",
    "access.users.new": "Новый пользователь",
    "access.users.edit": "Редактировать пользователя",
    "access.users.col.user": "Пользователь",
    "access.users.col.email": "Эл. почта",
    "access.users.col.group": "Группы",
    "access.users.col.active": "Активен",
    "access.users.active": "Активен",
    "access.users.inactive": "Неактивен",
    "access.users.activate": "Активировать",
    "access.users.deactivate": "Деактивировать",
    "access.users.noGroup": "Без группы",
    "access.users.empty": "Пока нет пользователей.",
    "access.users.emptyHint": "Создайте первого пользователя, чтобы предоставить доступ.",
    "access.users.field.name": "Полное имя",
    "access.users.field.namePlaceholder": "напр., I. Liepa",
    "access.users.field.username": "Имя пользователя",
    "access.users.field.usernamePlaceholder": "имя пользователя",
    "access.users.field.email": "Эл. почта",
    "access.users.field.lang": "Язык",
    "access.users.field.department": "Подразделение",
    "access.users.field.groups": "Группы разрешений",
    "access.users.field.groupsHint": "Пользователь наследует наиболее разрешающий уровень из всех своих групп.",
    "access.users.noDept": "Без подразделения",
    "access.users.create": "Создать пользователя",
    "access.users.save": "Сохранить изменения",
    "access.users.created": "Пользователь создан: {name}",
    "access.users.updated": "Пользователь обновлён: {name}",
    "access.users.saveErr": "Не удалось сохранить — проверьте права.",
    "access.users.createErr": "Не удалось создать пользователя.",
    "access.users.activated": "{name} активирован",
    "access.users.deactivated": "{name} деактивирован",
    "access.users.deactivateTitle": "Деактивировать пользователя",
    "access.users.deactivateConfirm":
      "Деактивировать «{name}»? Учётная запись и история сохраняются, но вход и назначение новых работ становятся недоступны.",
    "access.users.toggleErr": "Не удалось обновить пользователя.",
    "access.users.passwordNote":
      "Начальные пароли пользователь задаёт через выданный сервером сброс — эта форма никогда не отправляет открытый пароль.",
    "access.perms.group": "Группа разрешений",
    "access.perms.save": "Сохранить изменения",
    "access.perms.saveTitle": "Сохранить изменения разрешений",
    "access.perms.saveConfirm": "Применить изменённые уровни доступа к «{name}»? Это немедленно затронет всех участников группы.",
    "access.perms.saved": "Сохранены разрешения для {name}",
    "access.perms.saveErr": "Не удалось сохранить — проверьте права.",
    "access.perms.empty": "Нет групп разрешений.",
    "access.perms.emptyHint": "Сначала создайте группу в разделе «Группы пользователей (RBAC)».",
    "access.perms.menuVisible": "Видно в навигации",
    "access.perms.menuVisibleFor": "Видимость в меню для {module}",
    "access.perms.levelFor": "Уровень доступа {module}",
    "access.perms.note":
      "Уровни доступа применяются ко всем участникам группы. Просмотр = только чтение, Изменение = создание и изменение, Полный = включая удаление и настройку.",
    "access.perms.defaultsNote": "Показаны исходные уровни. Выберите уровень по каждому модулю и сохраните.",
    "access.companies.title": "Компании",
    "access.companies.add": "Добавить компанию",
    "access.companies.new": "Новая компания",
    "access.companies.edit": "Редактировать компанию",
    "access.companies.tenant": "один арендатор",
    "access.companies.col.name": "Компания",
    "access.companies.col.currency": "Валюта",
    "access.companies.col.active": "Активна",
    "access.companies.empty": "Пока нет компаний.",
    "access.companies.emptyHint": "Добавьте компанию, чтобы привязать пользователей и активы.",
    "access.companies.field.name": "Название компании",
    "access.companies.field.namePlaceholder": "напр., A/S ITERUM",
    "access.companies.field.currency": "Валюта",
    "access.companies.field.currencyPlaceholder": "напр., EUR",
    "access.companies.create": "Создать компанию",
    "access.companies.save": "Сохранить изменения",
    "access.companies.created": "Компания добавлена: {name}",
    "access.companies.updated": "Компания обновлена: {name}",
    "access.companies.saveErr": "Не удалось сохранить компанию.",
    "access.depts.title": "Подразделения",
    "access.depts.add": "Добавить подразделение",
    "access.depts.new": "Новое подразделение",
    "access.depts.edit": "Редактировать подразделение",
    "access.depts.hint": "Дочерние подразделения наследуют от родителя.",
    "access.depts.empty": "Пока нет подразделений.",
    "access.depts.emptyHint": "Добавьте первое подразделение, чтобы упорядочить активы и людей.",
    "access.depts.field.name": "Название подразделения",
    "access.depts.field.namePlaceholder": "напр., Механический отдел",
    "access.depts.field.parent": "Родительское подразделение",
    "access.depts.noParent": "Без родителя (верхний уровень)",
    "access.depts.create": "Создать подразделение",
    "access.depts.save": "Сохранить изменения",
    "access.depts.created": "Подразделение добавлено: {name}",
    "access.depts.updated": "Подразделение обновлено: {name}",
    "access.depts.saveErr": "Не удалось сохранить подразделение.",
    "access.depts.edit.btn": "Изменить",
    "access.depts.delete": "Удалить",
    "access.depts.deleteTitle": "Удалить подразделение",
    "access.depts.deleteConfirm":
      "Удалить «{name}»? Дочерние подразделения переносятся на верхний уровень; активы сохраняют свои записи.",
    "access.depts.deleted": "Подразделение удалено: {name}",
    "access.depts.deleteErr": "Не удалось удалить подразделение.",
    "tree.title": "Дерево объектов и устройств",
    "workboard.title": "Доска работ",
    "workboard.col.wo": "Наряд-заказ",
    "workboard.col.device": "Устройство",
    "workboard.col.status": "Статус",
    "workboard.col.due": "Срок · просрочка",
    "workboard.col.priority": "Приоритет",
    "workboard.filter.all": "Все",
    "workboard.filter.open": "Открытые",
    "workboard.filter.in_progress": "В работе",
    "workboard.filter.completed": "Завершённые",
    "workboard.empty": "Ничего не соответствует фильтру.",
    "workboard.sortBy": "Сортировать по",
    "workboard.bulk.title": "Массовые действия",
    "workboard.bulk.selected": "Выбрано: {n}",
    "workboard.bulk.selectAll": "Выбрать все наряд-заказы",
    "workboard.bulk.selectRow": "Выбрать наряд-заказ {wo}",
    "workboard.bulk.reassign": "Переназначить…",
    "workboard.bulk.reassignTitle": "Переназначить исполнителя",
    "workboard.bulk.reassignFrom": "Текущий исполнитель: {from}",
    "workboard.bulk.newPerformer": "Новый исполнитель",
    "workboard.bulk.selectUser": "Выберите…",
    "workboard.bulk.reassignNeedsOne": "Выберите строки с одним общим исполнителем для переназначения.",
    "workboard.bulk.reassignScopeNote":
      "Переназначение выполняется по исполнителю: ВСЕ предстоящие задачи этого исполнителя переходят к новому (не только выбранные строки). Завершённые задачи не затрагиваются.",
    "workboard.bulk.reassignConfirmTitle": "Подтвердить переназначение",
    "workboard.bulk.reassignConfirm": "Переназначить {n} предстоящих задач с {from} на {to}?",
    "workboard.bulk.reassigned": "Переназначено задач: {n}",
    "workboard.bulk.reschedule": "Перепланировать…",
    "workboard.bulk.rescheduleTitle": "Перепланировать наряд-заказы",
    "workboard.bulk.rescheduleDate": "Новый срок выполнения",
    "workboard.bulk.rescheduleAvoidWeekend": "Избегать выходных (Сб/Вс → пятница)",
    "workboard.bulk.rescheduleConfirmTitle": "Подтвердить перепланирование",
    "workboard.bulk.rescheduleConfirm": "Перенести {n} выбранных наряд-заказов на {date}?",
    "workboard.bulk.rescheduled": "Перепланировано наряд-заказов: {n}",
    "workboard.bulk.rescheduleNone": "Ни один из выбранных наряд-заказов нельзя перепланировать (завершённые/отменённые пропускаются).",
    "workboard.bulk.clear": "Снять выбор",
    // ---- WAVE 3: детали наряд-заказа ----
    "wo.details": "Детали",
    "wo.device": "Устройство",
    "wo.service": "Услуга",
    "wo.priority": "Приоритет",
    "wo.due": "Срок",
    "wo.performer": "Исполнитель",
    "wo.unassigned": "Не назначен",
    "wo.noDevice": "Объект",
    "wo.noLocation": "—",
    "wo.checklist": "Чек-лист",
    "wo.checklist.empty": "Шагов чек-листа пока нет.",
    "wo.checklist.add": "Добавить шаг",
    "wo.checklist.addPlaceholder": "Добавить шаг чек-листа…",
    "wo.checklist.delete": "Удалить шаг «{label}»",
    "wo.notes": "Заметки",
    "wo.notes.placeholder": "Сервисные заметки — что сделано, использованные детали, наблюдения…",
    "wo.notes.save": "Сохранить заметки",
    "wo.notesSaved": "Заметки сохранены",
    "wo.timer.label": "Таймер задачи",
    "wo.timer.running": "Идёт",
    "wo.timer.start": "Старт",
    "wo.timer.stop": "Стоп",
    "wo.timer.total": "Всего записано",
    "wo.timer.entries": "Записи учёта рабочего времени",
    "wo.timer.elapsed": "Прошло времени {time}",
    "wo.timerStarted": "Таймер запущен",
    "wo.timerStopped": "Таймер остановлен",
    "wo.timerAlreadyRunning": "У вас уже запущен таймер по этому наряд-заказу.",
    "wo.signOff": "Завершить и подписать",
    "wo.block.loto": "Заблокировано: сначала подтвердите блокировку/маркировку (LOTO).",
    "wo.block.noChecklist": "Заблокировано: добавьте хотя бы один шаг чек-листа.",
    "wo.block.checklist": "Заблокировано: должны быть выполнены все шаги чек-листа.",
    "wo.block.signed": "Заблокировано: наряд-заказ должен быть подписан.",
    // ---- WAVE 4 ----
    "register.title": "Реестр задач",
    "register.sub":
      "Полный стратегический реестр всех наряд-заказов — с постраничной выборкой по всему набору задач. Сортируйте, фильтруйте и экспортируйте; откройте любую строку для деталей.",
    "register.search": "Поиск по номеру или названию",
    "register.filter.status": "Фильтр статуса",
    "register.filter.priority": "Фильтр приоритета",
    "register.filter.allStatus": "Все статусы",
    "register.filter.allPriority": "Все приоритеты",
    "register.filter.overdue": "Только просроченные",
    "register.status.paused": "Приостановлен",
    "register.status.cancelled": "Отменён",
    "register.status.postponed": "Отложен",
    "register.prio.critical": "Критический",
    "register.prio.high": "Высокий",
    "register.prio.normal": "Обычный",
    "register.prio.low": "Низкий",
    "register.col.wo": "НЗ №",
    "register.col.title": "Название",
    "register.col.device": "Устройство",
    "register.col.status": "Статус",
    "register.col.priority": "Приоритет",
    "register.col.assignee": "Исполнитель",
    "register.col.due": "Срок",
    "register.col.overdue": "Просрочка",
    "register.unassigned": "Не назначен",
    "register.overdueFlag": "просрочен",
    "register.overdueDays": "+{n}д",
    "register.overdueTitle": "Срок истёк",
    "register.selectAll": "Выбрать все строки на этой странице",
    "register.selectRow": "Выбрать наряд-заказ {wo}",
    "register.export.all": "Экспорт CSV",
    "register.export.selected": "Экспорт CSV ({n})",
    "register.empty": "Нет наряд-заказов.",
    "register.emptyHint": "Ничего не соответствует текущим фильтрам на этой странице.",
    "register.range.of": "{from}–{to} из {total}",
    "register.range.window": "Показано {from}–{to}",
    "register.range.none": "Нет строк",
    "register.prev": "Назад",
    "register.next": "Вперёд",
    "status.open": "Открыт",
    "status.in_progress": "В работе",
    "status.paused": "Приостановлен",
    "status.completed": "Завершён",
    "status.cancelled": "Отменён",
    "status.postponed": "Отложен",
    "common.unknownUser": "(неизвестно)",
    "scheduler.service": "Обслуживание",
    "scheduler.priority": "Приоритет",
    "scheduler.close": "Закрыть",
    "downtimes.uncategorized": "(без категории)",
    "executors.title": "Исполнители и команды",
    "executors.sub":
      "Исполнители — это пользователи. Активируйте или деактивируйте их, редактируйте данные и добавляйте новых. Деактивированные исполнители исчезают из выбора для новых задач, но остаются во всех исторических записях.",
    "executors.add": "Добавить исполнителя",
    "executors.create": "Создать исполнителя",
    "executors.save": "Сохранить исполнителя",
    "executors.field.name": "Отображаемое имя",
    "executors.field.namePlaceholder": "Полное имя",
    "executors.field.username": "Имя пользователя",
    "executors.field.email": "Эл. почта",
    "executors.field.group": "Группа / роль",
    "executors.noGroup": "Без группы",
    "executors.active": "Активен",
    "executors.inactive": "Неактивен",
    "executors.activePerformer": "Активный исполнитель",
    "executors.activeHint":
      "Когда выключено, {name} исчезает из выбора для назначения новых задач — но каждый завершённый наряд-заказ сохраняет его имя для аудита.",
    "executors.empty": "Пока нет исполнителей.",
    "executors.selectHint": "Выберите исполнителя, чтобы просмотреть и изменить данные.",
    "executors.saved": "Сохранено: {name}",
    "executors.saveErr": "Не удалось сохранить — проверьте права.",
    "executors.created": "Исполнитель создан: {name}",
    "executors.createErr": "Не удалось создать исполнителя.",
    "executors.activated": "{name} активирован",
    "executors.deactivated": "{name} деактивирован",
    "groups.title": "Группы пользователей и роли (RBAC)",
    "groups.sub":
      "Группы доступа на основе ролей. Создавайте, переименовывайте и удаляйте группы здесь; полная матрица чтения/записи/удаления редактируется в разделе «Люди и доступ».",
    "groups.groups": "Группы",
    "groups.new": "Новая группа",
    "groups.newTitle": "Новая группа разрешений",
    "groups.editTitle": "Редактировать группу",
    "groups.field.name": "Название группы",
    "groups.field.namePlaceholder": "напр., Техник ночной смены",
    "groups.matrixNote": "Матрица чтения/записи/удаления настраивается в разделе «Люди и доступ».",
    "groups.members": "{n} пользователей",
    "groups.noPerms": "Разрешения ещё не назначены.",
    "groups.edit": "Изменить",
    "groups.delete": "Удалить",
    "groups.deleteTitle": "Удалить группу",
    "groups.deleteConfirm": "Удалить группу «{name}»? Пользователи сохранят учётные записи, но потеряют доступ этой группы.",
    "groups.create": "Создать группу",
    "groups.save": "Сохранить изменения",
    "groups.created": "Группа создана: {name}",
    "groups.updated": "Группа обновлена: {name}",
    "groups.deleted": "Группа удалена: {name}",
    "groups.saveErr": "Не удалось сохранить группу.",
    "groups.deleteErr": "Не удалось удалить группу.",
    "groups.empty": "Нет групп разрешений.",
    "groups.emptyHint": "Создайте первую группу, чтобы начать назначать доступ.",
    "common.close": "Закрыть",
    "nav.collapse": "Свернуть",
    "nav.expand": "Развернуть панель",
    "nav.autoCollapsed": "Автосворачивание (узко)",
    "profile.myProfile": "Мой профиль",
    "profile.preferences": "Настройки",
    "profile.toggleTheme": "Сменить тему",
    "profile.group": "Группа",
    "profile.company": "Компания",
    "profile.username": "Имя пользователя",
    "profile.email": "Эл. почта",
    "profile.language": "Язык",
    "settings.nav.title": "Навигация",
    "settings.nav.layout": "Раскладка навигации",
    "settings.nav.layoutHint":
      "Выберите, где расположено меню. Боковая панель удобна для плотной работы планировщика; верхняя панель освобождает место по вертикали для таблиц и схем.",
    "settings.nav.side": "Боковая панель",
    "settings.nav.top": "Верхняя панель",
    "settings.nav.width": "Ширина панели",
    "settings.nav.widthHint":
      "Развёрнутая показывает подписи; свёрнутая — панель иконок с подсказками. Автосворачивание в узких окнах.",
    "settings.nav.expanded": "Развёрнутая",
    "settings.nav.collapsed": "Свёрнутая",
    "downtimes.title": "Журнал простоев",
    "downtimes.kpi.total": "Общий простой",
    "downtimes.kpi.events": "Зарегистрировано событий",
    "downtimes.kpi.down": "Сейчас в простое",
    "downtimes.kpi.mtbf": "MTBF · среднее время между отказами",
    "downtimes.kpi.availability": "Готовность",
    "downtimes.kpi.na": "н/д",
    "downtimes.kpi.naHint": "Требуется хотя бы один закрытый простой.",
    "downtimes.byCause": "По причине",
    "downtimes.empty": "Простои ещё не зарегистрированы.",
    "downtimes.emptyHint": "Зарегистрируйте первый период простоя, чтобы рассчитать готовность и MTBF.",
    "downtimes.noCats": "Категории простоев не заданы.",
    "downtimes.col.actions": "Действия",
    "downtimes.convert": "Преобразовать в наряд",
    "downtimes.convertTitle": "Преобразовать простой в наряд?",
    "downtimes.convertBody": "Создать новый наряд по этому отказу ({asset}). Он откроется в Рабочей доске.",
    "downtimes.convertConfirm": "Создать наряд",
    "downtimes.converted": "Наряд {number} создан",
    "downtimes.convertErr": "Не удалось преобразовать простой в наряд.",
    "downtimes.convertNoPerm": "У вас нет прав на создание нарядов.",
    "downtimes.ongoing": "продолжается",
    "downtimes.statusLabel": "Статус",
    "downtimes.resolved": "Устранён",
    "inventory.title": "Склад запчастей",
    "inventory.available": "Доступно",
    "inventory.booked": "Зарезервировано",
    "inventory.inStock": "На складе",
    "inventory.noSpares": "Запчастей пока нет.",
    "inventory.noSparesHint": "Добавьте запчасти в справочниках, чтобы резервировать, принимать и перемещать остатки.",
    "reports.title": "Отчёты",
    "reports.empty": "Нет строк для этих фильтров.",
    "reports.emptyHint": "Расширьте диапазон дат или выберите другой отчёт.",
    "reports.col.deviceId": "Оборудование",
    "reports.col.userId": "Техник",
    "reports.col.taskId": "Наряд",
    "reports.col.failures": "Отказы",
    "reports.col.downtimeHours": "Простой",
    "reports.col.repairHours": "Время ремонта",
    "reports.col.workHours": "Время работы",
    "reports.col.startedAt": "Начато",
    "reports.col.endedAt": "Завершено",
    "reports.col.reason": "Причина",
    "reports.col.spare": "Деталь",
    "reports.col.name": "Название",
    "reports.col.movementType": "Движение",
    "reports.col.quantity": "Кол-во",
    "reports.col.lineValue": "Стоимость",
    "reports.col.docDate": "Дата",
    "reports.col.number": "№",
    "reports.col.title": "Название",
    "reports.col.status": "Статус",
    "reports.col.priority": "Приоритет",
    "reports.col.completedAt": "Завершено",
    "nav.dashboard": "Отчёты и панель",
    "dashboard.title": "Отчёты и панель",
    "dashboard.sub":
      "Состояние обслуживания завода в одном представлении — готовность, MTBF, соблюдение графика и расходы на запчасти, рассчитанные из реальных отчётов. Неустановимые значения показывают «н/д», а не выдуманные числа.",
    "dashboard.na": "н/д",
    "dashboard.naHint": "Пока не вычислимо из доступных данных отчётов.",
    "dashboard.kpi.uptime": "Готовность оборудования",
    "dashboard.kpi.mtbf": "MTBF · среднее время между отказами",
    "dashboard.kpi.completion": "Завершённые наряд-заказы",
    "dashboard.kpi.partsSpend": "Расходы на запчасти (закупки)",
    "dashboard.repairs": "Ремонты — плановые и внеплановые",
    "dashboard.planned": "Плановые",
    "dashboard.unplanned": "Внеплановые",
    "dashboard.noRepairs": "Пока нет завершённых наряд-заказов для графика.",
    "dashboard.downtimeByLine": "Простои по оборудованию",
    "dashboard.totalDowntime": "всего",
    "dashboard.noDowntime": "Простои ещё не зарегистрированы.",
    "dashboard.topAssets": "Оборудование с наибольшей нагрузкой по обслуживанию",
    "dashboard.topAssetsHint": "по суммарным часам простоя",
    "dashboard.noAssets": "Пока нет простоев оборудования для ранжирования.",
    "dashboard.assetMeta": "{failures} отказ(ов)",
    "dashboard.assetLoad": "{hours} ч простоя",
    "common.loading": "Загрузка…",
    "nav.review": "Центр проверки",
    "review.title": "Центр проверки",
    "chrome.search": "Поиск",
    "chrome.offline": "Не в сети (кэш)",
    "chrome.pending": "ожидает синхронизации",
    // ---- WAVE 7: шаблоны измерений + показания ----
    "nav.measTemplates": "Шаблоны измерений",
    "nav.meters": "Показания счётчиков",
    "measTpl.title": "Шаблоны измерений",
    "measTpl.sub": "Задайте допусковые зоны LL/L/H/HH для каждой точки измерения, сгруппированные в шаблоны, привязанные к категориям устройств.",
    "measTpl.list": "Шаблоны",
    "measTpl.add": "Новый шаблон",
    "measTpl.addTitle": "Новый шаблон измерений",
    "measTpl.name": "Название шаблона",
    "measTpl.namePlaceholder": "напр., Вибрация экструдера",
    "measTpl.category": "Категория устройства",
    "measTpl.categoryNone": "— (не привязан)",
    "measTpl.empty": "Шаблонов измерений пока нет",
    "measTpl.emptyHint": "Создайте шаблон, затем добавьте точки измерения с допусковыми зонами.",
    "measTpl.rename": "Переименовать шаблон",
    "measTpl.deleteTitle": "Удалить шаблон?",
    "measTpl.deleteBody": "Это удалит шаблон и все его точки измерения. Записанные показания сохранятся.",
    "measTpl.selectHint": "Выберите шаблон для редактирования точек",
    "measTpl.points": "Точки измерения",
    "measTpl.pointAdd": "Добавить точку",
    "measTpl.pointName": "Название точки",
    "measTpl.pointNamePlaceholder": "напр., Подшипник DE",
    "measTpl.unit": "Ед. изм.",
    "measTpl.unitPlaceholder": "напр., мм/с",
    "measTpl.pointsEmpty": "В этом шаблоне пока нет точек",
    "measTpl.pointsEmptyHint": "Добавьте точку с порогами LL/L/H/HH; полоса предпросмотра покажет зоны.",
    "measTpl.pointEdit": "Изменить точку",
    "measTpl.pointDeleteTitle": "Удалить точку?",
    "measTpl.pointDeleteBody": "Это удалит точку измерения и её допусковые зоны.",
    "measTpl.preview": "Предпросмотр зон",
    "measTpl.thresholdOrder": "Пороги должны удовлетворять LL ≤ L ≤ H ≤ HH.",
    "measTpl.col.point": "Точка",
    "measTpl.col.unit": "Ед.",
    "measTpl.col.bands": "LL / L / H / HH",
    "meters.title": "Показания счётчиков",
    "meters.sub": "Выберите устройство, затем запишите показание для каждой его точки измерения. Каждое значение проверяется по допусковой зоне в реальном времени.",
    "meters.device": "Устройство",
    "meters.devicePlaceholder": "Выберите устройство…",
    "meters.noDevice": "Выберите устройство для записи показаний",
    "meters.noDeviceHint": "Здесь загрузятся его точки измерения (из шаблона привязанной категории).",
    "meters.noPoints": "У этого устройства нет точек измерения",
    "meters.noPointsHint": "Привяжите категорию устройства к шаблону измерений с точками и вернитесь.",
    "meters.point": "Точка",
    "meters.latest": "Последнее",
    "meters.newValue": "Новое показание",
    "meters.record": "Записать",
    "meters.recorded": "Показание записано",
    "meters.inTolerance": "В допуске",
    "meters.warn": "Вне зоны предупреждения",
    "meters.alarm": "Вне зоны тревоги — проверьте",
    "meters.noReading": "Показаний ещё нет",
    "meters.recent": "Недавние показания",
    "meters.recentEmpty": "Показаний ещё не записано",
    "meters.noPerm": "У вас нет прав на запись показаний.",
    // ---- admin console ----
    "admin.title": "Консоль администратора",
    "admin.sub": "Массовые операции, переключатели функций по арендаторам, безопасность (разрешение/блокировка IP + журнал аудита) и центр интеграции с Dynamics 365 Business Central.",
    "admin.tabsLabel": "Вкладки администратора",
    "admin.tab.flags": "Переключатели функций",
    "admin.tab.mass": "Массовая замена",
    "admin.tab.security": "Безопасность",
    "admin.tab.bc": "Dynamics 365 BC",
    "admin.flag.enabled": "{name} включено",
    "admin.flag.disabled": "{name} отключено",
    "admin.flag.toggleErr": "Не удалось переключить — проверьте свои права.",
    "admin.mass.step.current": "Текущий исполнитель",
    "admin.mass.step.scope": "Область",
    "admin.mass.step.new": "Новый исполнитель",
    "admin.mass.step.confirm": "Подтверждение",
    "admin.mass.select": "Выберите…",
    "admin.mass.filterLoc": "Фильтр по местоположению (необязательно)",
    "admin.mass.allLocations": "Все местоположения",
    "admin.mass.reassigned": "Переназначено задач: {n}",
    "admin.mass.reassignErr": "Не удалось переназначить — проверьте свои права.",
    "admin.mass.confirmLead": "Переназначить",
    "admin.mass.confirmTrail": "предстоящих задач новому исполнителю. Завершённые задачи не затрагиваются.",
    "admin.mass.back": "Назад",
    "admin.mass.continue": "Продолжить",
    "admin.mass.transfer": "Передать задач: {n}",
    "admin.sec.whitelisted": "В белом списке {cidr}",
    "admin.sec.invalidCidr": "Неверный CIDR / IP.",
    "admin.sec.addErr": "Не удалось добавить диапазон.",
    "admin.sec.unblockTitle": "Разблокировать {ip}?",
    "admin.sec.unblockBody": "Этому IP снова будет разрешена аутентификация.",
    "admin.sec.unblockConfirm": "Разблокировать IP",
    "admin.sec.unblocked": "Разблокирован {ip}",
    "admin.sec.unblockErr": "Не удалось разблокировать.",
    "admin.sec.testInvalidIp": "Введите один адрес IPv4 (без CIDR).",
    "admin.sec.testErr": "Не удалось проверить IP.",
    "admin.sec.exportErr": "Не удалось экспортировать — проверьте свои права.",
    "admin.sec.blocklist": "Активный список блокировки",
    "admin.sec.blockedCount": "заблокировано: {n}",
    "admin.sec.colIp": "IP-адрес",
    "admin.sec.colReason": "Причина",
    "admin.sec.unblock": "Разблокировать",
    "admin.sec.noBlocked": "Нет заблокированных адресов",
    "admin.sec.testTitle": "Проверить IP",
    "admin.sec.testHint": "проверить разрешение / блокировку по текущим правилам",
    "admin.sec.testInputLabel": "IP-адрес для проверки",
    "admin.sec.testPlaceholder": "напр. 10.4.22.187",
    "admin.sec.test": "Проверить",
    "admin.sec.wouldBe": "будет",
    "admin.sec.allowed": "РАЗРЕШЁН",
    "admin.sec.blocked": "ЗАБЛОКИРОВАН",
    "admin.sec.reasonAllowlisted": "(исключён через разрешённый диапазон {ranges})",
    "admin.sec.reasonBlocked": "(совпало с правилом блокировки {rules})",
    "admin.sec.reasonDefaultAllow": "(ни одно правило не совпало — разрешено по умолчанию)",
    "admin.sec.allowedRanges": "Разрешённые диапазоны (CIDR)",
    "admin.sec.removeRange": "Удалить {cidr}",
    "admin.sec.newRangeLabel": "Новый диапазон CIDR",
    "admin.sec.cidrPlaceholder": "напр. 172.16.0.0/12",
    "admin.sec.addRange": "Добавить диапазон",
    "admin.sec.auditTrail": "Журнал аудита",
    "admin.sec.exportCsv": "Экспорт CSV",
    "admin.sec.filterFrom": "С",
    "admin.sec.filterTo": "По",
    "admin.sec.filterActor": "ID исполнителя",
    "admin.sec.filterActorPlaceholder": "ID пользователя / API-ключа",
    "admin.sec.filterAction": "Действие",
    "admin.sec.filterActionPlaceholder": "напр. Blocked",
    "admin.sec.noDiff": "Журнал аудита не записывает детали старое → новое значение; столбцы ниже — всё, что фиксируется.",
    "admin.sec.colTime": "Время",
    "admin.sec.colActor": "Исполнитель",
    "admin.sec.colTarget": "Цель",
    "admin.sec.colIpShort": "IP",
    "admin.sec.colAction": "Действие",
    "admin.sec.tagMobile": "мобильное",
    "admin.sec.noAudit": "Нет записей аудита по текущим фильтрам.",
    "admin.bc.title": "Синхронизация позиций · Dynamics 365 BC",
    "admin.bc.note": "Идентификатор продукта BC доступен только для чтения — мы привязываемся к нему, но никогда не переименовываем. Перегенерация владеющего API-ключа прекращает интеграцию, поэтому её привязки вычисляются как Осиротевшие.",
    "admin.bc.colSpare": "Запчасть",
    "admin.bc.colProductId": "ID продукта Dynamics",
    "admin.bc.colStatus": "Статус",
    "admin.bc.state.synced": "Синхронизировано",
    "admin.bc.state.out_of_sync": "Не синхронизировано",
    "admin.bc.state.orphaned": "Осиротевшее",
    "admin.bc.empty": "Пока нет привязок запчасть↔позиция. Свяжите запчасть с позицией BC, чтобы начать синхронизацию.",
    // ---- review center ----
    "review.sub": "Офлайн-изменения, которые не удалось применить автоматически — их цель была удалена или переназначена. Примените, чтобы провести их принудительно, или отклоните.",
    "review.reason.target_deleted": "Цель удалена",
    "review.reason.target_reassigned": "Переназначено другому",
    "review.tab.open": "Открытые",
    "review.tab.resolved": "Решённые",
    "review.emptyOpen": "Нет отложенных изменений — всё синхронизировано чисто.",
    "review.emptyResolved": "Пока нет решённых элементов.",
    "review.applyTitle": "Применить отложенное изменение?",
    "review.discardTitle": "Отклонить отложенное изменение?",
    "review.applyBody": "Принудительно применить эту операцию {op} к {table}. Это переопределяет удаление/переназначение, которое её отложило.",
    "review.discardBody": "Отклонить эту операцию {op} к {table}. Отложенное изменение будет удалено.",
    "review.applyConfirm": "Применить",
    "review.discardConfirm": "Отклонить",
    "review.applied": "Изменение применено",
    "review.discarded": "Изменение отклонено",
    "review.notPermitted": "Не разрешено (нужен полный доступ к задачам)",
    "review.resolveErr": "Не удалось разрешить ({code})",
    "review.colChange": "Изменение",
    "review.colWas": "Было",
    "review.apply": "Применить",
    "review.discard": "Отклонить",
    "review.resolved": "решено",
    // ---- device card ----
    "dc.close": "Закрыть",
    "dc.nfc": "NFC",
    "dc.sectionsLabel": "Разделы карточки устройства",
    "dc.status.operational": "В работе",
    "dc.status.maintenance": "Обслуживание",
    "dc.status.down": "Простой",
    "dc.status.decommissioned": "Выведено из эксплуатации",
    "dc.noOpenWos": "Нет открытых рабочих заданий.",
    "dc.maint.intervals": "Интервалы обслуживания",
    "dc.maint.kind.counter": "Счётчик",
    "dc.maint.kind.time": "Время",
    "dc.maint.onSchedule": "по графику",
    "dc.maint.none": "Нет запланированных интервалов обслуживания.",
    "dc.measure.points": "Точки измерения",
    "dc.measure.noTemplate": "К этому устройству не привязан шаблон измерений.",
    "dc.measure.normal": "норма",
    "dc.downtime.log": "Журнал простоев",
    "dc.downtime.default": "Простой",
    "dc.downtime.ongoing": "продолжается",
    "dc.downtime.min": "{n} мин",
    "dc.files.documents": "Документы",
    "dc.files.colFile": "Файл",
    "dc.files.colType": "Тип",
    "dc.files.colAdded": "Добавлено",
    "dc.files.namePlaceholder": "инструкция.pdf",
    "dc.files.urlPlaceholder": "https://… или data:…",
    "dc.history.timeline": "Единая хронология",
    "dc.spares.mapped": "Привязанные запчасти",
    "dc.spares.hint": "Доступно = На складе − Зарезервировано (зарезервировано по открытым заданиям)",
    "dc.spares.colCode": "Код",
    "dc.spares.colPart": "Деталь",
    "dc.spares.colInStock": "На складе",
    "dc.spares.colBooked": "Зарезервировано",
    "dc.spares.colAvailable": "Доступно",
    "dc.spares.colMin": "Мин.",
    "dc.spares.colStatus": "Статус",
    "dc.spares.belowMin": "Ниже минимума",
    "dc.spares.ok": "OK",
    "dc.spares.none": "К этому устройству не привязаны запчасти.",
    // ---- command palette / notifications ----
    "cmdk.label": "Командная палитра",
    "cmdk.placeholder": "Поиск активов, рабочих заданий, запчастей, людей…",
    "cmdk.searchLabel": "Поиск",
    "cmdk.resultsLabel": "Результаты поиска",
    "cmdk.hint": "Начните вводить, чтобы искать по всему заводу.",
    "cmdk.esc": "Esc",
    "cmdk.noMatches": "Нет совпадений для «{q}».",
    "notif.label": "Уведомления",
    "notif.unread": "непрочитано",
    "notif.bell": "Уведомления",
    "notif.bellUnread": "Уведомления ({n} непрочитанных)",
    "notif.markAll": "Отметить все как прочитанные",
    "notif.empty": "Вы всё просмотрели.",
    "notif.ago.justNow": "только что",
    "notif.ago.min": "{n} мин назад",
    "notif.ago.hour": "{n} ч назад",
    "notif.ago.day": "{n} дн назад",
  },
};

/** Interpolate `{var}` placeholders. */
function interpolate(text: string, vars?: TVars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name]) : whole));
}

/**
 * Translate a key: core dictionary first (shared strings), then the web
 * extension (active lang → EN), then the key itself. Never throws.
 */
export function tWeb(lang: Lang, key: string, vars?: TVars): string {
  // core `t()` returns the key unchanged when missing — detect that to fall through.
  const core = coreT(lang, key, vars);
  if (core !== key) return core;
  const hit = WEB_STRINGS[lang]?.[key] ?? WEB_STRINGS.en[key];
  return hit ? interpolate(hit, vars) : key;
}

/** Hook: translator bound to the active language (live-switching). */
export function useScopedT(): (key: string, vars?: TVars) => string {
  const { lang } = usePrefs();
  return (key, vars) => tWeb(lang, key, vars);
}
